import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import axios from 'axios';
import DOMPurify from 'dompurify';
import './Calendar.css';

const CalendarComponent = () => {
  const { fileName } = useParams();
  const [searchParams] = useSearchParams();
  
  // Function to normalize filename for comparison - handles both spaces and hyphens
  const normalizeFileName = (fileName) => {
    if (!fileName) return '';
    return fileName
      .replace(/\.xlsx$/i, '')
      .replace(/[-_]*\d{4}[-_]*$/, '') // Remove year suffixes like -2025, ---2025, _2025
      .replace(/[-_\s]+/g, ' ') // Replace all hyphens, underscores, and multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  // Function to clean filename - remove .xlsx extension, year suffixes, and convert hyphens back to spaces
  const cleanFileName = (rawFileName) => {
    if (!rawFileName) return null;
    // Convert hyphens back to spaces, remove .xlsx extension, and remove year suffixes
    return rawFileName
      .replace(/-/g, ' ')
      .replace(/\.xlsx$/i, '')
      .replace(/[-_]*\d{4}[-_]*$/, '') // Remove year suffixes like -2025, ---2025, _2025
      .replace(/\s+$/, ''); // Remove trailing spaces
  };
  
  const selectedFileName = cleanFileName(fileName);
  
  // Debug logging
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('4days');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsRefreshKey, setEventsRefreshKey] = useState(0); // Force refresh mechanism
  const [isCreatingEvent, setIsCreatingEvent] = useState(false); // Prevent duplicate reloads
  const [lastCreatedRowcolEvent, setLastCreatedRowcolEvent] = useState(null); // Track last rowcol event
  const [lastCreatedEvent, setLastCreatedEvent] = useState(null); // Track last created event
  const [lastContentType, setLastContentType] = useState(null); // Track last content type
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventData, setEditEventData] = useState({
    title: '',
    start: new Date(),
    end: new Date(Date.now() + 60 * 60 * 1000),
    location: '',
    description: '',
    guests: '',
    calendar: 'Primary'
  });
  
  // State for direct event creation
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [createEventData, setCreateEventData] = useState({
    title: '',
    description: '',
    location: '',
    guests: '',
    contentType: 'content', // 'content' or 'rowcol'
    content: '', // for CKEditor content
    rows: 1,
    cols: 1,
    dynamicRows: [], // for storing rows with multiple input boxes
    eventDate: selectedDate.toISOString().split('T')[0], // YYYY-MM-DD format
    startTime: '09:00', // HH:MM format
    // endTime removed - only using start time
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [customDaysCount, setCustomDaysCount] = useState(4);
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationShown = useRef(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [timeSlotsRef, setTimeSlotsRef] = useState(null);
  const [daysGridRef, setDaysGridRef] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Table state
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [showTableEditModal, setShowTableEditModal] = useState(false);
  const [editingData, setEditingData] = useState({});
  
  // Inline editing state
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [inlineEditingData, setInlineEditingData] = useState({});
  const inputRefs = useRef({});
  
  // CKEditor state for scheduling
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [schedulingMode, setSchedulingMode] = useState('add'); // 'add', 'edit'
  const [schedulingData, setSchedulingData] = useState({
    cellA: '',
    cellB: '',
    cellC: '',
    cellD: '',
    cellE: ''
  });
  const [editingSchedulingIndex, setEditingSchedulingIndex] = useState(null);
  
  // CKEditor state for non-date sheets
  const [nonDateSheetContent, setNonDateSheetContent] = useState('');
  const [isEditingNonDateSheet, setIsEditingNonDateSheet] = useState(false);
  const [editingNonDateSheetId, setEditingNonDateSheetId] = useState(null);
  
  // CKEditor state for content details editing
  const [isEditingContentDetails, setIsEditingContentDetails] = useState(false);
  const [editingContentDetailsId, setEditingContentDetailsId] = useState(null);
  const [contentDetailsData, setContentDetailsData] = useState({});
  
  // Memoized onChange handlers to prevent infinite loops
  const handleCellAChange = useCallback((content) => {
    setSchedulingData(prev => ({...prev, cellA: content}));
  }, []);

  const handleCellBChange = useCallback((content) => {
    setSchedulingData(prev => ({...prev, cellB: content}));
  }, []);

  const handleCellCChange = useCallback((content) => {
    setSchedulingData(prev => ({...prev, cellC: content}));
  }, []);

  const handleCellDChange = useCallback((content) => {
    setSchedulingData(prev => ({...prev, cellD: content}));
  }, []);

  const handleCellEChange = useCallback((content) => {
    setSchedulingData(prev => ({...prev, cellE: content}));
  }, []);
  
  // CKEditor handlers for non-date sheets
  const handleNonDateSheetEdit = useCallback((eventData) => {
    setEditingNonDateSheetId(eventData.id);
    // Combine all fields into a single content string for editing
    const combinedContent = Object.entries(eventData.extendedProps.allFields)
      .filter(([key, value]) => value && value !== '')
      .map(([key, value]) => `<p><strong>${key}:</strong> ${String(value)}</p>`)
      .join('');
    setNonDateSheetContent(combinedContent);
    setIsEditingNonDateSheet(true);
  }, []);
  
  const handleNonDateSheetSave = useCallback(async () => {
    if (!editingNonDateSheetId) return;
    
    try {
      // Here you would typically save to your backend
      // For now, we'll just update the local state
      
      // Close editing mode
      setIsEditingNonDateSheet(false);
      setEditingNonDateSheetId(null);
      setNonDateSheetContent('');
      
      // Show success message
      alert('Content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content. Please try again.');
    }
  }, [editingNonDateSheetId, nonDateSheetContent]);
  
  const handleNonDateSheetCancel = useCallback(() => {
    setIsEditingNonDateSheet(false);
    setEditingNonDateSheetId(null);
    setNonDateSheetContent('');
  }, []);
  
  // CKEditor handlers for content details editing
  const handleEditContentDetails = useCallback(() => {
    if (!selectedEvent) return;
    
    setEditingContentDetailsId(selectedEvent.id);
    
    // Initialize content details data with current values
    const initialData = {};
    
    // Check if it's a single row content type or allFields type
    if (selectedEvent.extendedProps && selectedEvent.extendedProps.allFields) {
      // Handle allFields case
      Object.entries(selectedEvent.extendedProps.allFields).forEach(([key, value]) => {
        if (key !== 'cellD' && key !== 'cellE' && value && value.trim() !== '') {
          initialData[key] = value;
        }
      });
    } else if (selectedEvent.extendedProps && selectedEvent.extendedProps.rows && selectedEvent.extendedProps.rows.length === 1) {
      // Handle single row case
      const row = selectedEvent.extendedProps.rows[0];
      if (row.cellData) {
        Object.entries(row.cellData).forEach(([key, value]) => {
          if (key !== 'cellD' && key !== 'cellE' && value && value.trim() !== '') {
            initialData[key] = value;
          }
        });
      }
    }
    
    setContentDetailsData(initialData);
    setIsEditingContentDetails(true);
  }, [selectedEvent]);
  
  const handleContentDetailsSave = useCallback(async () => {
    if (!editingContentDetailsId) return;
    
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save changes', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Find the event being edited
      const eventToUpdate = events.find(event => event.id === editingContentDetailsId);
      if (!eventToUpdate || !eventToUpdate.extendedProps) {
        showNotification('Event not found', 'error');
        return;
      }

      let response;
      
      // Get the fileId from the event structure
      const fileId = eventToUpdate.extendedProps.fileId || 
                    eventToUpdate.extendedProps.excelFileId || 
                    eventToUpdate.extendedProps.fileId;
      
      if (!fileId) {
        showNotification('File ID not found in event data', 'error');
        return;
      }

      if (eventToUpdate.extendedProps.allFields) {
        // Handle allFields case - update via API
        const updatedAllFields = {
          ...eventToUpdate.extendedProps.allFields,
          ...contentDetailsData
        };

        response = await fetch(baseUrl + `/api/excel-data/${fileId}/allFields`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            allFields: updatedAllFields,
            sheetName: eventToUpdate.extendedProps.sheetName
          })
        });
      } else if (eventToUpdate.extendedProps.rows && eventToUpdate.extendedProps.rows.length === 1) {
        // Handle single row case - update via API
        const updatedRowData = {
          ...eventToUpdate.extendedProps.rows[0].cellData,
          ...contentDetailsData
        };

        response = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            rowIndex: eventToUpdate.extendedProps.rows[0].rowIndex,
            cellData: updatedRowData,
            sheetName: eventToUpdate.extendedProps.sheetName
          })
        });
      } else {
        showNotification('Invalid event structure for editing', 'error');
        return;
      }

        if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save content details');
      }

      // Update local state after successful API call
      const updatedEvents = events.map(event => {
        if (event.id === editingContentDetailsId) {
          if (event.extendedProps && event.extendedProps.allFields) {
            // Handle allFields case
            return {
              ...event,
              extendedProps: {
                ...event.extendedProps,
                allFields: {
                  ...event.extendedProps.allFields,
                  ...contentDetailsData
                }
              }
            };
          } else if (event.extendedProps && event.extendedProps.rows && event.extendedProps.rows.length === 1) {
            // Handle single row case
            return {
              ...event,
              extendedProps: {
                ...event.extendedProps,
                rows: event.extendedProps.rows.map((row, index) => {
                  if (index === 0) {
                    return {
                      ...row,
                      cellData: {
                        ...row.cellData,
                        ...contentDetailsData
                      }
                    };
                  }
                  return row;
                })
              }
            };
          }
        }
        return event;
      });
      
      // Update the events state
      setEvents(updatedEvents);
      
      // Update the selectedEvent state to reflect the changes immediately
      const updatedSelectedEvent = updatedEvents.find(event => event.id === editingContentDetailsId);
      if (updatedSelectedEvent) {
        setSelectedEvent(updatedSelectedEvent);
      }
      
      setIsEditingContentDetails(false);
      setEditingContentDetailsId(null);
      setContentDetailsData({});
      showNotification('Content details updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving content details:', error);
      showNotification(`Error saving content details: ${error.message}`, 'error');
    }
  }, [editingContentDetailsId, contentDetailsData, events]);
  
  const handleContentDetailsCancel = useCallback(() => {
    setIsEditingContentDetails(false);
    setEditingContentDetailsId(null);
    setContentDetailsData({});
  }, []);
  
  const eventColors = [
    '#8b1634', '#ea4335', '#34a853', '#fbbc04', '#ff6d01', 
    '#9c27b0', '#00bcd4', '#795548', '#607d8b', '#e91e63',
    '#ff5722', '#4caf50', '#2196f3', '#ff9800', '#9c27b0',
    '#f44336', '#00bcd4', '#8bc34a', '#ffc107', '#673ab7',
    '#e91e63', '#009688', '#cddc39', '#ffeb3b', '#3f51b5',
    '#ff1744', '#00e676', '#ff3d00', '#651fff', '#1de9b6',
    '#ff6f00', '#00b0ff', '#c6ff00', '#ff4081', '#18ffff'
  ];

  // Function to get next available unique color
  const getNextUniqueColor = () => {
    const usedColors = events.map(event => event.color);
    const availableColors = eventColors.filter(color => !usedColors.includes(color));
    
    if (availableColors.length > 0) {
      return availableColors[0];
    }
    
    // If all colors are used, cycle through them
    return eventColors[events.length % eventColors.length];
  };

  // Function to generate unique color for each sheet based on sheet name
  const getSheetColor = (sheetName) => {
    if (!sheetName) return '#3788d8'; // Default color
    
    // Create a simple hash from the sheet name
    let hash = 0;
    for (let i = 0; i < sheetName.length; i++) {
      const char = sheetName.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Use absolute value and modulo to get a consistent index
    const colorIndex = Math.abs(hash) % eventColors.length;
    return eventColors[colorIndex];
  };

  // Function to detect if sheetName is a date format
  const isDateSheetName = (sheetName) => {
    if (!sheetName || typeof sheetName !== 'string') {
      return false;
    }
    
    // Common date patterns like "Thurs 2nd Jan 2025", "Thurs 2nd Jan", "2nd Jan 2025", etc.
    const datePatterns = [
      // Day of week + day + month + year (optional)
      /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+\d{1,2}(st|nd|rd|th)?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)(\s+\d{4})?$/i,
      // Day + month + year (optional)
      /^\d{1,2}(st|nd|rd|th)?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)(\s+\d{4})?$/i,
      // Month + day + year (optional)
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}(st|nd|rd|th)?(\s+\d{4})?$/i,
      // More flexible patterns
      /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+\d{1,2}(st|nd|rd|th)?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
      /^\d{1,2}(st|nd|rd|th)?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}(st|nd|rd|th)?/i
    ];
    
    const trimmedName = sheetName.trim();
    const isDate = datePatterns.some(pattern => pattern.test(trimmedName));
    
    // Also try a more flexible approach - check if it contains month names and numbers
    if (!isDate) {
      const hasMonth = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)/i.test(trimmedName);
      const hasNumber = /\d{1,2}/.test(trimmedName);
      const hasDayOfWeek = /(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/i.test(trimmedName);
      
      if ((hasMonth && hasNumber) || (hasDayOfWeek && hasNumber)) {
        return true;
      }
    }
    
    return isDate;
  };

  // Function to parse date from sheetName
  const parseDateFromSheetName = (sheetName) => {
    if (!isDateSheetName(sheetName)) {
      return null;
    }
    
    try {
      // Clean up the sheetName and add year if missing
      let cleanSheetName = sheetName.trim();
      
      // If year is missing, add current year
      if (!/\d{4}/.test(cleanSheetName)) {
        cleanSheetName += ` ${new Date().getFullYear()}`;
      }
      
      // Try to parse the date
      const parsedDate = new Date(cleanSheetName);
      
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        // Try alternative parsing methods
        const alternativeDate = tryAlternativeDateParsing(cleanSheetName);
        if (alternativeDate) {
          return alternativeDate;
        }
        return null;
      }
      
      return parsedDate;
    } catch (error) {
      return null;
    }
  };

  // Function to try alternative date parsing methods
  const tryAlternativeDateParsing = (dateString) => {
    try {
      // Remove ordinal suffixes (st, nd, rd, th)
      const cleanedString = dateString.replace(/(\d+)(st|nd|rd|th)/g, '$1');
      
      // Try parsing again
      const parsedDate = new Date(cleanedString);
      
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
      
      // Try with different date formats
      const formats = [
        cleanedString,
        cleanedString.replace(/(\w+)\s+(\d+)\s+(\w+)/, '$2 $3'), // "Jan 2nd 2025" -> "2 Jan 2025"
        cleanedString.replace(/(\w+)\s+(\d+)\s+(\w+)/, '$3 $2'), // "Jan 2nd 2025" -> "2025 2"
      ];
      
      for (const format of formats) {
        const testDate = new Date(format);
        if (!isNaN(testDate.getTime())) {
          return testDate;
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Parse dates from various formats to JavaScript Date objects
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Handle different date formats
    if (typeof dateStr === 'string') {
      // Try DD/MM/YYYY format first (most common in your data)
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return new Date(year, month - 1, day);
        }
      }
      // Try DD-MM-YYYY format
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return new Date(year, month - 1, day);
        }
      }
      // Try YYYY-MM-DD format
      if (dateStr.includes('-') && dateStr.length === 10) {
        return new Date(dateStr);
      }
    }
    
    // If it's already a Date object or timestamp
    if (dateStr instanceof Date) {
      return dateStr;
    }
    
    // Try to parse as Date
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Guest management functions
  const addGuest = (email, name = '') => {
    const guest = {
      id: Date.now(),
      email,
      name: name || email.split('@')[0],
      rsvp: 'pending',
      responseDate: null
    };
    setGuestList(prev => [...prev, guest]);
    return guest;
  };

  const updateGuestRSVP = (guestId, rsvp) => {
    setGuestList(prev => prev.map(guest => 
      guest.id === guestId 
        ? { ...guest, rsvp, responseDate: new Date() }
        : guest
    ));
  };

  const removeGuest = (guestId) => {
    setGuestList(prev => prev.filter(guest => guest.id !== guestId));
  };

  // Notification system
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    const notification = { id, message, type, timestamp: new Date() };
    setNotifications(prev => [...prev, notification]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Scroll synchronization between time slots and days grid
  useEffect(() => {
    if (timeSlotsRef && daysGridRef) {
      const handleTimeSlotsScroll = () => {
        if (daysGridRef.scrollTop !== timeSlotsRef.scrollTop) {
          daysGridRef.scrollTop = timeSlotsRef.scrollTop;
        }
      };

      const handleDaysGridScroll = () => {
        if (timeSlotsRef.scrollTop !== daysGridRef.scrollTop) {
          timeSlotsRef.scrollTop = daysGridRef.scrollTop;
        }
      };

      timeSlotsRef.addEventListener('scroll', handleTimeSlotsScroll);
      daysGridRef.addEventListener('scroll', handleDaysGridScroll);

      return () => {
        timeSlotsRef.removeEventListener('scroll', handleTimeSlotsScroll);
        daysGridRef.removeEventListener('scroll', handleDaysGridScroll);
      };
    }
  }, [timeSlotsRef, daysGridRef]);


  // Handle URL date parameter for navigation
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      try {
        const parsedDate = new Date(dateParam);
        if (!isNaN(parsedDate.getTime())) {
          setCurrentDate(parsedDate);
          setSelectedDate(parsedDate);
        }
      } catch (error) {
        console.error('Error parsing date parameter:', error);
      }
    }
  }, [searchParams]);

  // Load events from backend on component mount and when selectedFileName changes
  useEffect(() => {
    loadEventsFromBackend();
  }, [selectedFileName]);

  // Fetch teams data
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) return;
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/team`, config);
        
        if (response.data.success) {
          setTeams(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };

    fetchTeams();
  }, []);


  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Function to truncate title based on word count
  const truncateTitle = (title) => {
    if (!title) return '';
    const words = title.trim().split(/\s+/);
    if (words.length > 3) {
      return words.slice(0, 3).join(' ') + '...';
    }
    return title;
  };

  // Function to get team color by team name
  const getTeamColor = (teamName) => {
    if (!teamName || !teams.length) return null;
    const team = teams.find(t => t.teamName === teamName);
    return team ? team.color : null;
  };

  // Helper function to check if sheet name contains a date
  const isDateSheet = (sheetName) => {
    if (!sheetName) return false;
    // Check for common date patterns in sheet names
    const datePatterns = [
      /(mon|tue|wed|thu|fri|sat|sun)/i,  // Day abbreviations
      /(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,  // Full day names
      /\d{1,2}(st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,  // Date with month
      /\d{1,2}\/\d{1,2}\/\d{2,4}/,  // MM/DD/YYYY or DD/MM/YYYY
      /\d{1,2}-\d{1,2}-\d{2,4}/,  // MM-DD-YYYY or DD-MM-YYYY
      /\d{4}-\d{1,2}-\d{1,2}/,  // YYYY-MM-DD
      /(thurs|thu)\s+\d{1,2}(st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,  // "Thurs 4th Sept" pattern
      /(weds|wed)\s+\d{1,2}(st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,  // "Weds 30th July" pattern
    ];
    return datePatterns.some(pattern => pattern.test(sheetName));
  };

  // Filter table data based on search term
  const filteredTableData = selectedEvent?.extendedProps?.rows?.filter(row => {
    if (!tableSearchTerm) return true;
    const searchLower = tableSearchTerm.toLowerCase();
    return Object.values(row.cellData || {}).some(value => 
      String(value).toLowerCase().includes(searchLower)
    );
  }) || [];

  // Handle edit row
  const handleEditRow = (row, index) => {
    setEditingRow({ row, index });
    setEditingData({ ...row.cellData });
    setShowTableEditModal(true);
  };

  // Handle edit all records
  const handleEditAllRecords = () => {
    // Enable inline editing mode
    setIsInlineEditing(true);
    
    // Initialize inline editing data with current table data
    const initialData = {};
    if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.rows) {
      selectedEvent.extendedProps.rows.forEach((row, rowIndex) => {
        if (row.cellData) {
          initialData[rowIndex] = { ...row.cellData };
        }
      });
    }
    setInlineEditingData(initialData);
  };

  // Handle edit specific record directly
  const handleEditRecordDirect = (row, index) => {
    setEditingRow({ row, index });
    setEditingData({ ...row.cellData });
    setShowTableEditModal(true);
  };

  // Handle inline cell value change
  const handleInlineCellChange = (rowIndex, cellKey, value) => {
    setInlineEditingData(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [cellKey]: value
      }
    }));
  };

  // Handle input change for regular input fields
  const handleInputChange = (e, rowIndex, cellKey) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    handleInlineCellChange(rowIndex, cellKey, value);
    
    // Restore cursor position after state update
    setTimeout(() => {
      const inputRef = inputRefs.current[`${rowIndex}-${cellKey}`];
      if (inputRef && inputRef.setSelectionRange) {
        inputRef.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };


  // Save inline editing changes
  const handleSaveInlineEditing = async () => {
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save changes', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Find the event being edited
      const eventToUpdate = selectedEvent;
      if (!eventToUpdate || !eventToUpdate.extendedProps) {
        showNotification('Event not found', 'error');
        return;
      }

      // Check multiple possible locations for fileId
      const fileId = eventToUpdate.extendedProps.fileId || 
                    eventToUpdate.extendedProps.excelFileId ||
                    eventToUpdate.extendedProps.file?._id ||
                    eventToUpdate.extendedProps.file?.id ||
                    eventToUpdate.fileId ||
                    eventToUpdate.id;
      
      
      if (!fileId) {
        showNotification('File ID not found in event data', 'error');
        return;
      }
      
      // Validate that fileId is a valid MongoDB ObjectId (24 character hex string)
      if (!/^[0-9a-fA-F]{24}$/.test(fileId)) {
        console.error('❌ Invalid fileId format:', fileId, 'Expected 24 character hex string');
        showNotification(`Invalid file ID format: ${fileId}`, 'error');
        return;
      }

      const sheetName = eventToUpdate.extendedProps.sheetName || 'Sheet1';
      
      
      // Show loading notification
      showNotification('Saving changes...', 'info');
      

        // Update rows sequentially to avoid version conflicts
        const results = [];
        const entries = Object.entries(inlineEditingData);
        
        for (let i = 0; i < entries.length; i++) {
          const [frontendIndex, cellData] = entries[i];
          
          try {
            // Get the actual row from the original data
            const originalRow = selectedEvent.extendedProps.rows[parseInt(frontendIndex)];
            
            if (!originalRow) {
              throw new Error(`Original row not found for frontend index ${frontendIndex}`);
            }
            
            // Use the actual rowIndex from the original row data
            // This ensures we're using the correct rowIndex that exists in the database
            const actualRowIndex = originalRow.rowIndex;
            
            // Debug logging for row indexing
            
            if (actualRowIndex === undefined || actualRowIndex === null) {
              throw new Error(`Row index not found for frontend index ${frontendIndex} - originalRow.rowIndex is ${actualRowIndex}`);
            }
            
            
            // Add a small delay between requests to avoid version conflicts
            if (i > 0) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            const response = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                rowIndex: actualRowIndex,
                cellData: cellData,
                sheetName: sheetName
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('❌ API Error:', errorData);
              
              // If row not found, try to create it first
              if (errorData.message && errorData.message.includes('not found')) {
                
                // Try to create the row first
                const createResponse = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    rowIndex: actualRowIndex,
                    cellData: cellData,
                    sheetName: sheetName
                  })
                });
                
                if (createResponse.ok) {
                  // Row was created successfully, no need to update again
                  results.push({ success: true, rowIndex: parseInt(frontendIndex) });
                  continue; // Skip to next iteration
                } else {
                  const createErrorData = await createResponse.json();
                  throw new Error(`Row ${actualRowIndex}: Failed to create - ${createErrorData.message || 'Unknown error'}`);
                }
              } else {
              throw new Error(`Row ${actualRowIndex}: ${errorData.message || 'Failed to save'}`);
              }
            }

            const responseData = await response.json();

            results.push({ success: true, rowIndex: parseInt(frontendIndex) });
          } catch (error) {
            console.error(`Error updating row ${frontendIndex}:`, error);
            results.push({ success: false, rowIndex: parseInt(frontendIndex), error: error.message });
          }
        }
        
        // Check if any updates failed
        const failedUpdates = results.filter(result => !result.success);
        if (failedUpdates.length > 0) {
          const errorMessages = failedUpdates.map(f => f.error).join(', ');
          throw new Error(`Some updates failed: ${errorMessages}`);
        }

        // Update local state only if all API calls succeeded
        setEvents(prev => prev.map(event => {
          if (event.id === selectedEvent.id) {
            return {
              ...event,
              extendedProps: {
                ...event.extendedProps,
                rows: event.extendedProps.rows.map((row, index) => ({
                  ...row,
                  cellData: inlineEditingData[index] || row.cellData
                }))
              }
            };
          }
          return event;
        }));

        // Update selectedEvent state
        const updatedSelectedEvent = {
          ...selectedEvent,
          extendedProps: {
            ...selectedEvent.extendedProps,
            rows: selectedEvent.extendedProps.rows.map((row, index) => ({
              ...row,
              cellData: inlineEditingData[index] || row.cellData
            }))
          }
        };
        setSelectedEvent(updatedSelectedEvent);

        // Show success notification and then make table uneditable
        showNotification(`Successfully updated ${results.length} records!`, 'success');
        
        // Exit inline editing mode after success notification
        setIsInlineEditing(false);
        setInlineEditingData({});

    } catch (error) {
      console.error('Error saving inline editing:', error);
      showNotification(`Error saving changes: ${error.message}`, 'error');
    }
  };

  // Cancel inline editing
  const handleCancelInlineEditing = () => {
    setIsInlineEditing(false);
    setInlineEditingData({});
  };

  // Delete individual row during inline editing
  const handleDeleteRowInline = async (rowIndex) => {
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to delete row', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Find the event being edited
      const eventToUpdate = selectedEvent;
      if (!eventToUpdate || !eventToUpdate.extendedProps) {
        showNotification('Event not found', 'error');
        return;
      }

      // Check multiple possible locations for fileId
      const fileId = eventToUpdate.extendedProps.fileId || 
                    eventToUpdate.extendedProps.excelFileId ||
                    eventToUpdate.extendedProps.file?._id ||
                    eventToUpdate.extendedProps.file?.id ||
                    eventToUpdate.fileId ||
                    eventToUpdate.id;
      
      if (!fileId) {
        showNotification('File ID not found in event data', 'error');
        return;
      }

      const sheetName = eventToUpdate.extendedProps.sheetName || 'Sheet1';
      const originalRow = selectedEvent.extendedProps.rows[parseInt(rowIndex)];
      
      if (!originalRow) {
        showNotification('Row not found', 'error');
        return;
      }

      // Use frontend index + 1 as the actual row index
      // The frontend array index corresponds to the actual row position in the database
      const actualRowIndex = parseInt(rowIndex) + 1;
      
      if (actualRowIndex === undefined || actualRowIndex === null) {
        showNotification('Row index not found', 'error');
        return;
      }

      // Debug logging

      // Show confirmation
      if (!window.confirm('Are you sure you want to delete this row?')) {
        return;
      }

      showNotification('Deleting row...', 'info');

      // Delete the row from backend
      const response = await fetch(baseUrl + `/api/excel-data/${fileId}/rows/${actualRowIndex}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sheetName: sheetName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete row');
      }

      // Update local state - remove the row from the data
      const updatedRows = selectedEvent.extendedProps.rows.filter((_, index) => index !== parseInt(rowIndex));
      
      setEvents(prev => prev.map(event => {
        if (event.id === selectedEvent.id) {
          return {
            ...event,
            extendedProps: {
              ...event.extendedProps,
              rows: updatedRows
            }
          };
        }
        return event;
      }));

      // Update selectedEvent state
      const updatedSelectedEvent = {
        ...selectedEvent,
        extendedProps: {
          ...selectedEvent.extendedProps,
          rows: updatedRows
        }
      };
      setSelectedEvent(updatedSelectedEvent);

      // Remove from inline editing data
      const newInlineEditingData = { ...inlineEditingData };
      delete newInlineEditingData[rowIndex];
      setInlineEditingData(newInlineEditingData);

        showNotification('Row deleted successfully!', 'success');
        
        // Make table uneditable after success notification
        setIsInlineEditing(false);
        setInlineEditingData({});
    } catch (error) {
      console.error('Error deleting row:', error);
      showNotification(`Error deleting row: ${error.message}`, 'error');
    }
  };

  // Handle delete row
  const handleDeleteRow = (row, index) => {
    setEventToDelete({ row, index, type: 'tableRow' });
    setShowDeleteConfirmation(true);
  };

  // Handle save edited row
  const handleSaveEdit = async () => {
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save changes', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Find the event being edited
      const eventToUpdate = selectedEvent;
      if (!eventToUpdate || !eventToUpdate.extendedProps) {
        showNotification('Event not found', 'error');
        return;
      }

      // Check multiple possible locations for fileId
      const fileId = eventToUpdate.extendedProps.fileId || 
                    eventToUpdate.extendedProps.excelFileId ||
                    eventToUpdate.extendedProps.file?._id ||
                    eventToUpdate.extendedProps.file?.id ||
                    eventToUpdate.fileId ||
                    eventToUpdate.id;
      
      
      if (!fileId) {
        showNotification('File ID not found in event data', 'error');
        return;
      }
      
      // Validate that fileId is a valid MongoDB ObjectId (24 character hex string)
      if (!/^[0-9a-fA-F]{24}$/.test(fileId)) {
        console.error('❌ Invalid fileId format:', fileId, 'Expected 24 character hex string');
        showNotification(`Invalid file ID format: ${fileId}`, 'error');
        return;
      }

      const sheetName = eventToUpdate.extendedProps.sheetName || 'Sheet1';
      
      // Get the original row to find the correct rowIndex
      const originalRow = selectedEvent.extendedProps.rows[editingRow.index];
      if (!originalRow) {
        showNotification('Original row not found', 'error');
        return;
      }

      const actualRowIndex = originalRow.rowIndex;
      
      showNotification('Saving changes...', 'info');

      // Make API call to update the row
      const response = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rowIndex: actualRowIndex,
          cellData: editingData,
          sheetName: sheetName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        
        // If row not found, try to create it first
        if (errorData.message && errorData.message.includes('not found')) {
          
          // Try to create the row first
          const createResponse = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              rowIndex: actualRowIndex,
              cellData: editingData,
              sheetName: sheetName
            })
          });
          
          if (!createResponse.ok) {
            const createErrorData = await createResponse.json();
            throw new Error(`Failed to create row: ${createErrorData.message || 'Unknown error'}`);
          }
          
        } else {
          throw new Error(errorData.message || 'Failed to save row');
        }
      }

      // Update local state only after successful API call
      const updatedRows = [...selectedEvent.extendedProps.rows];
      updatedRows[editingRow.index] = {
        ...updatedRows[editingRow.index],
        cellData: editingData
      };
      
      // Update the selected event
      setSelectedEvent({
        ...selectedEvent,
        extendedProps: {
          ...selectedEvent.extendedProps,
          rows: updatedRows
        }
      });
      
      setShowTableEditModal(false);
      setEditingRow(null);
      setEditingData({});
      showNotification('Record updated successfully', 'success');
    } catch (error) {
      console.error('Error updating record:', error);
      showNotification(`Error updating record: ${error.message}`, 'error');
    }
  };

  // Handle save individual record
  const handleSaveRecord = async (row, index) => {
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save changes', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Find the event being edited
      const eventToUpdate = selectedEvent;
      if (!eventToUpdate || !eventToUpdate.extendedProps) {
        showNotification('Event not found', 'error');
        return;
      }

      // Check multiple possible locations for fileId
      const fileId = eventToUpdate.extendedProps.fileId || 
                    eventToUpdate.extendedProps.excelFileId ||
                    eventToUpdate.extendedProps.file?._id ||
                    eventToUpdate.extendedProps.file?.id ||
                    eventToUpdate.fileId ||
                    eventToUpdate.id;
      
      
      if (!fileId) {
        showNotification('File ID not found in event data', 'error');
        return;
      }
      
      // Validate that fileId is a valid MongoDB ObjectId (24 character hex string)
      if (!/^[0-9a-fA-F]{24}$/.test(fileId)) {
        console.error('❌ Invalid fileId format:', fileId, 'Expected 24 character hex string');
        showNotification(`Invalid file ID format: ${fileId}`, 'error');
        return;
      }

      const sheetName = eventToUpdate.extendedProps.sheetName || 'Sheet1';
      
      // Use the actual rowIndex from the row data
      const actualRowIndex = row.rowIndex;
      
      showNotification('Saving changes...', 'info');

      // Make API call to update the row
      const response = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rowIndex: actualRowIndex,
          cellData: row.cellData,
          sheetName: sheetName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        
        // If row not found, try to create it first
        if (errorData.message && errorData.message.includes('not found')) {
          
          // Try to create the row first
          const createResponse = await fetch(baseUrl + `/api/excel-data/${fileId}/rows`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              rowIndex: actualRowIndex,
              cellData: row.cellData,
              sheetName: sheetName
            })
          });
          
          if (!createResponse.ok) {
            const createErrorData = await createResponse.json();
            throw new Error(`Failed to create row: ${createErrorData.message || 'Unknown error'}`);
          }
          
        } else {
          throw new Error(errorData.message || 'Failed to save row');
        }
      }

      showNotification(`Record ${actualRowIndex} updated successfully`, 'success');
    } catch (error) {
      console.error('Error updating record:', error);
      showNotification(`Error updating record: ${error.message}`, 'error');
    }
  };

  // Handle save all records
  const handleSaveAllRecords = async () => {
    try {
      // Here you would typically make an API call to update the database
      // For now, we'll just show a success message
      showNotification('All records updated successfully', 'success');
      setShowTableEditModal(false);
    } catch (error) {
      console.error('Error updating records:', error);
      showNotification('Error updating records', 'error');
    }
  };

  // CKEditor Scheduling Handlers
  const handleAddScheduling = () => {
    setSchedulingMode('add');
    setSchedulingData({
      cellA: '',
      cellB: '',
      cellC: '',
      cellD: '',
      cellE: ''
    });
    setEditingSchedulingIndex(null);
    setShowSchedulingModal(true);
  };

  const handleEditScheduling = (row, index) => {
    setSchedulingMode('edit');
    setSchedulingData({ ...row.cellData });
    setEditingSchedulingIndex(index);
      setShowSchedulingModal(true);
  };

  const handleDeleteScheduling = (row, index) => {
    setEventToDelete({ row, index, type: 'schedulingRow' });
    setShowDeleteConfirmation(true);
  };

  // Helper function to strip HTML tags and clean text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary div element to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    // Get text content and clean up whitespace
    return (temp.textContent || temp.innerText || '').trim();
  };

  const sanitizeHtmlForRender = (html) => {
    if (!html) return '';
    return DOMPurify.sanitize(String(html), { USE_PROFILES: { html: true } });
  };

  // Helper function to clean cell data (keep HTML for display, but clean empty values)
  const cleanCellData = (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const cellValue = data[key];
      // Keep HTML formatting but check if there's actual content
      if (cellValue && cellValue.trim() !== '') {
        const textContent = stripHtmlTags(cellValue);
        // Only include if there's actual text content (not just HTML tags or whitespace)
        if (textContent && textContent.replace(/\s/g, '') !== '') {
          cleaned[key] = cellValue; // Keep the original HTML for display
        }
      }
    });
    return cleaned;
  };

  // Helper function to check if a cell has meaningful content
  const hasCellContent = (cellValue) => {
    if (!cellValue) return false;
    const cleaned = stripHtmlTags(cellValue);
    // Check if the cleaned value has any non-whitespace characters
    return cleaned && cleaned.trim() !== '' && cleaned.replace(/\s/g, '') !== '';
  };

    // Helper function to check if cell should be hidden
    const shouldHideCell = (cellValue) => {
      if (!cellValue) return true;
      
      // Hide rowcol values
      if (cellValue === 'rowcol') return true;
      
      // Hide date strings (ISO format)
      if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return true;
      }
      
      // Hide empty strings or strings with only whitespace
      if (typeof cellValue === 'string' && cellValue.trim() === '') {
        return true;
      }
      
      // Hide HTML content that has no meaningful text
      const cleaned = stripHtmlTags(cellValue);
      if (cleaned && cleaned.trim() === '') {
        return true;
      }
      
      return false;
  };

  // Helper function to check if column should be hidden
  const shouldHideColumn = (column) => {
    // Hide columns that contain rowcol or date values
    if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.rows) {
      return selectedEvent.extendedProps.rows.some(row => {
        const cellValue = row.cellData?.[column];
        if (!cellValue) return false;
        const value = cellValue.toString().trim().toLowerCase();
        return value === 'rowcol' || value.includes('2025-09-06t14:00:00');
      });
    }
      return false;
  };

  // Helper function to get available columns based on data
  const getAvailableColumns = (data) => {
    const columns = [];
    
    // For UKPN events, show specific columns
    if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.isUkpnEvent) {
      // Show these columns: Time, Area, Site Details, Post Code, Date Visited, PO Number, Invoice No
      const ukpnColumns = ['cellH', 'cellB', 'cellC', 'cellD', 'cellE', 'cellI', 'cellK', 'cellL']; // H=Time, B=Priorities, C=Area, D=Site Details, E=Post Code, I=Date Visited, K=PO Number, L=Invoice No
      
      ukpnColumns.forEach(key => {
        // Check if any row has meaningful content in this column
        const hasData = data.some(row => {
          const cellValue = row.cellData?.[key];
          if (!cellValue) return false;
          
          // Strip HTML tags and check for meaningful content
          const textContent = stripHtmlTags(cellValue);
          return textContent && 
                 textContent !== 'N/A' && 
                 textContent !== '' && 
                 textContent.trim() !== '' &&
                 textContent.replace(/\s/g, '') !== '' &&
                 hasCellContent(cellValue) && 
                 !shouldHideCell(cellValue);
        });
        
        if (hasData) {
          columns.push(key);
        }
      });
    }
    // For Southeastern events, show only 5 specific columns
    else if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.isSoutheasternEvent) {
      // Only show these 5 columns: taylorTechnologyNumber, clientReferenceNo, locations, time, descriptionOfWorks
      const southeasternColumns = ['cellG', 'cellD', 'cellE', 'cellF', 'cellN']; // G=Time, D=TT No, E=Client Ref, F=Location, N=Description
      
      southeasternColumns.forEach(key => {
        // Check if any row has meaningful content in this column
        const hasData = data.some(row => {
          const cellValue = row.cellData?.[key];
          // For Southeastern events, check if the value is not empty string or 'N/A'
          if (!cellValue) return false;
          
          // Strip HTML tags and check for meaningful content
          const textContent = stripHtmlTags(cellValue);
          return textContent && 
                 textContent !== 'N/A' && 
                 textContent !== '' && 
                 textContent.trim() !== '' &&
                 textContent.replace(/\s/g, '') !== '' &&
                 hasCellContent(cellValue) && 
                 !shouldHideCell(cellValue);
        });
        
        if (hasData) {
          columns.push(key);
        }
      });
    } else {
      // For other events, use limited columns
      const cellKeys = ['cellA', 'cellB', 'cellC', 'cellD', 'cellE', 'cellF'];
    
    cellKeys.forEach(key => {
      // Check if any row has meaningful content in this column
      const hasData = data.some(row => {
        const cellValue = row.cellData?.[key];
        return hasCellContent(cellValue) && !shouldHideCell(cellValue);
      });
      
      if (hasData) {
        columns.push(key);
      }
    });
    }
    
    return columns;
  };

  // Helper function to get column display names for UKPN and Southeastern events
  const getColumnDisplayName = (column, data) => {
    // Check if this is a UKPN event
    if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.isUkpnEvent) {
      // Define the field names for UKPN events based on column position
      const ukpnFieldNames = {
        cellA: 'Date',
        cellB: 'Priorities',
        cellC: 'Area',
        cellD: 'Site Details',
        cellE: 'Post Code',
        cellF: 'W3W',
        cellG: 'Budget Holder',
        cellH: 'Start Time',
        cellI: 'Date Visited',
        cellJ: 'Maintenance Cost',
        cellK: 'PO Number',
        cellL: 'Invoice No',
        cellM: 'Comments 2025',
        cellN: 'Additional Info/Comments',
        cellO: 'Additional Comment'
      };
      
      // Return the field name for this column
      if (ukpnFieldNames[column]) {
        return ukpnFieldNames[column];
      }
    }
    // Check if this is a Southeastern event by looking for the isSoutheasternEvent flag
    else if (selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.isSoutheasternEvent) {
      // Define the field names for Southeastern events based on column position
      // These should match the actual API field names from the Southeastern record
      // Excluding: Created, Updated, Schedule Date, Type, Subtype
      const southeasternFieldNames = {
        cellA: 'Date Received',
        cellB: 'Date Scheduled', 
        cellC: 'Priority',
        cellD: 'TT No.',
        cellE: 'Client Refer No',
        cellF: 'Location',
        cellG: 'Start Time',
        cellH: 'Site Details',
        cellI: 'Additional Notes',
        cellJ: 'Quotation No',
        cellK: 'Value',
        cellL: 'Job No',
        cellM: 'Station',
        cellN: 'Description',
        cellO: 'CH Notes',
        cellP: 'Simon Notes',
        cellQ: 'Return Visits',
        cellR: 'Cherry Picker',
        cellS: 'Return Site Details',
        cellT: 'Return Additional Notes',
        cellU: 'Return For Images',
        cellV: 'PPM Comment'
      };
      
      // Return the field name for this column
      if (southeasternFieldNames[column]) {
        return southeasternFieldNames[column];
      }
    }
    
    // Fallback to generic column name
    return column.replace('cell', 'Column ');
  };

  const handleSaveScheduling = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save changes', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Clean the cell data before saving (remove HTML tags and empty values)
      const cleanedCellData = cleanCellData(schedulingData);

      if (schedulingMode === 'add') {
        // Add new scheduling record
        const response = await fetch(baseUrl + `/api/excel-data/${selectedEvent.extendedProps.fileId}/rows`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            sheetName: selectedEvent.extendedProps.sheetName,
            cellData: cleanedCellData
          })
        });

        if (response.ok) {
          const result = await response.json();
        const newRow = {
            _id: Date.now().toString(),
            rowIndex: result.data.rowIndex,
            cellData: { ...cleanedCellData }
        };
        
          // Update local state
        const updatedRows = [...selectedEvent.extendedProps.rows, newRow];
        setSelectedEvent({
          ...selectedEvent,
          extendedProps: {
            ...selectedEvent.extendedProps,
            rows: updatedRows
          }
        });

        showNotification('Scheduling record added successfully', 'success');
        } else {
          const errorData = await response.json();
          showNotification(`Error adding record: ${errorData.message || 'Unknown error'}`, 'error');
        }
      } else {
        // Edit existing scheduling record
        const rowToUpdate = selectedEvent.extendedProps.rows[editingSchedulingIndex];
        
        const response = await fetch(baseUrl + `/api/excel-data/${selectedEvent.extendedProps.fileId}/rows`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            sheetName: selectedEvent.extendedProps.sheetName,
            rowIndex: rowToUpdate.rowIndex,
            cellData: cleanedCellData
          })
        });

        if (response.ok) {
          // Update local state
        const updatedRows = selectedEvent.extendedProps.rows.map((row, index) => 
          index === editingSchedulingIndex 
              ? { ...row, cellData: cleanedCellData }
            : row
        );
        
        setSelectedEvent({
          ...selectedEvent,
          extendedProps: {
            ...selectedEvent.extendedProps,
            rows: updatedRows
          }
        });

        showNotification('Scheduling record updated successfully', 'success');
        } else {
          const errorData = await response.json();
          showNotification(`Error updating record: ${errorData.message || 'Unknown error'}`, 'error');
        }
      }
      
      setShowSchedulingModal(false);
      setSchedulingData({
        cellA: '',
        cellB: '',
        cellC: '',
        cellD: '',
        cellE: ''
      });
      setEditingSchedulingIndex(null);
    } catch (error) {
      console.error('Error saving scheduling:', error);
      showNotification('Error saving scheduling record', 'error');
    }
  };

  const handleCloseSchedulingModal = () => {
    setShowSchedulingModal(false);
    setSchedulingData({
      cellA: '',
      cellB: '',
      cellC: '',
      cellD: '',
      cellE: ''
    });
    setEditingSchedulingIndex(null);
  };

  // Handle confirm delete row
  const handleConfirmDeleteRow = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to delete records', 'error');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      const rowToDelete = selectedEvent.extendedProps.rows[eventToDelete.index];
      
      const response = await fetch(baseUrl + `/api/excel-data/${selectedEvent.extendedProps.fileId}/rows`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sheetName: selectedEvent.extendedProps.sheetName,
          rowIndex: rowToDelete.rowIndex
        })
      });

      if (response.ok) {
        // Update local state
      const updatedRows = selectedEvent.extendedProps.rows.filter((_, index) => 
        index !== eventToDelete.index
      );
      
      setSelectedEvent({
        ...selectedEvent,
        extendedProps: {
          ...selectedEvent.extendedProps,
          rows: updatedRows
        }
      });
      
      setShowDeleteConfirmation(false);
      setEventToDelete(null);
      
      const recordType = eventToDelete.type === 'schedulingRow' ? 'Scheduling record' : 'Record';
        showNotification(`${recordType} deleted successfully`, 'success');
      } else {
        const errorData = await response.json();
        showNotification(`Error deleting record: ${errorData.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error deleting record', 'error');
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getDaysForView = (date, viewType) => {
      const startDate = new Date(date);
    
    switch (viewType) {
      case 'day':
        return [startDate];
      
      case '2days':
      case '3days':
      case '4days':
      case '5days':
      case '6days':
      case '7days':
        const dayCount = parseInt(viewType.replace('days', ''));
      const days = [];
        
        // Start from the selected date and show the next N days
        for (let i = 0; i < dayCount; i++) {
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + i);
        days.push(day);
      }
      return days;
      
      case 'week':
        const weekStart = new Date(startDate);
        const weekDayOfWeek = weekStart.getDay();
        const weekDaysToSubtract = weekDayOfWeek === 0 ? 6 : weekDayOfWeek - 1; // Start from Monday
        weekStart.setDate(weekStart.getDate() - weekDaysToSubtract);
        
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
          const day = new Date(weekStart);
          day.setDate(weekStart.getDate() + i);
          weekDays.push(day);
        }
        return weekDays;
      
      case 'month':
    return getDaysInMonth(date);
      
      case 'year':
        // For year view, return all months of the year
        const year = date.getFullYear();
        const yearMonths = [];
        for (let month = 0; month < 12; month++) {
          yearMonths.push(new Date(year, month, 1));
        }
        return yearMonths;
      
      default:
        return getDaysInMonth(date);
    }
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 24; hour++) {
      slots.push({
        hour: hour,
        time: hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`
      });
    }
    return slots;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    
    const dayEvents = events.filter(event => {
      const eventDate = new Date(event.start);
      const isMatch = eventDate.toDateString() === date.toDateString();
      
      // DEBUG: Only log for UPS BOX events
      if (isMatch && event.description && event.description.includes('UPS BOX')) {
      }
      
      return isMatch;
    });
    
    return dayEvents;
  };

  // Function to group overlapping events
  const groupOverlappingEvents = (events) => {
    if (events.length === 0) return [];
    
    const sortedEvents = events.sort((a, b) => new Date(a.start) - new Date(b.start));
    const groups = [];
    let currentGroup = [sortedEvents[0]];
    
    for (let i = 1; i < sortedEvents.length; i++) {
      const currentEvent = sortedEvents[i];
      const lastEventInGroup = currentGroup[currentGroup.length - 1];
      
      // Check if events overlap
      if (new Date(currentEvent.start) < new Date(lastEventInGroup.end)) {
        currentGroup.push(currentEvent);
      } else {
        groups.push(currentGroup);
        currentGroup = [currentEvent];
      }
    }
    
    groups.push(currentGroup);
    return groups;
  };

  // Function to generate recurring events
  const generateRecurringEvents = (event, endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)) => {
    if (!event.recurring || event.recurringPattern === 'none') {
      return [event];
    }

    const recurringEvents = [event];
    const startDate = new Date(event.start);
    const currentDate = new Date(startDate);
    const interval = event.recurringInterval || 1;

    while (currentDate < endDate) {
      const nextDate = new Date(currentDate);
      
      switch (event.recurringPattern) {
        case 'daily':
          nextDate.setDate(currentDate.getDate() + interval);
          break;
        case 'weekly':
          nextDate.setDate(currentDate.getDate() + (7 * interval));
          break;
        case 'monthly':
          nextDate.setMonth(currentDate.getMonth() + interval);
          break;
        case 'yearly':
          nextDate.setFullYear(currentDate.getFullYear() + interval);
          break;
        default:
          return recurringEvents;
      }

      if (nextDate <= endDate) {
        const newEvent = {
          ...event,
          id: `${event.id}_${nextDate.getTime()}`,
          start: new Date(nextDate.getTime() + (event.start.getTime() - startDate.getTime())),
          end: new Date(nextDate.getTime() + (event.end.getTime() - startDate.getTime()))
        };
        recurringEvents.push(newEvent);
        currentDate.setTime(nextDate.getTime());
      } else {
        break;
      }
    }

    return recurringEvents;
  };

  // Function to handle drag and drop
  const handleEventDragStart = (event, e) => {
    e.preventDefault();
    setDraggedEvent(event);
    setDragStart({ x: e.clientX, y: e.clientY });
    setIsDragging(true);
  };

  const handleEventDragEnd = (e) => {
    if (!draggedEvent || !dragStart) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    // Calculate new time based on drag distance
    const timeSlotHeight = 60; // Height of each time slot in pixels
    const timeDelta = Math.round(deltaY / timeSlotHeight) * 60 * 60 * 1000; // Convert to milliseconds
    
    const newStart = new Date(draggedEvent.start.getTime() + timeDelta);
    const newEnd = new Date(draggedEvent.end.getTime() + timeDelta);

    // Update the event
    setEvents(prev => prev.map(event => 
      event.id === draggedEvent.id 
        ? { ...event, start: newStart, end: newEnd }
        : event
    ));

    setDraggedEvent(null);
    setDragStart(null);
    setIsDragging(false);
  };

  // Function to handle event resizing
  const handleEventResize = (event, direction, e) => {
    e.stopPropagation();
    const timeSlotHeight = 60;
    const deltaY = e.clientY - e.target.getBoundingClientRect().top;
    const timeDelta = Math.round(deltaY / timeSlotHeight) * 60 * 60 * 1000;

    let newStart = new Date(event.start);
    let newEnd = new Date(event.end);

    if (direction === 'start') {
      newStart = new Date(event.start.getTime() + timeDelta);
    } else {
      newEnd = new Date(event.end.getTime() + timeDelta);
    }

    if (newStart < newEnd) {
      setEvents(prev => prev.map(evt => 
        evt.id === event.id 
          ? { ...evt, start: newStart, end: newEnd }
          : evt
      ));
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setCurrentDate(date);
    // Reload events when clicking on a date to ensure fresh data
    loadEventsFromBackend();
  };

  const handleEventClick = (event) => {
    
    // For UKPN events, show detailed information
    if (event.extendedProps?.isUkpnEvent) {
      
      const recordData = event.extendedProps.recordData;
      const scheduleData = event.extendedProps.scheduleData;
      
      // Create a detailed event for display
      const ukpnEvent = {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          isUkpnEvent: true,
          contentType: 'ukpn',
          sheetName: 'UKPN PPM Schedule',
          rows: [
            {
              rowIndex: 1,
              cellData: {
                cellA: `${recordData.date || ''}`,
                cellB: `${recordData.priorities || ''}`,
                cellC: `${recordData.area || ''}`,
                cellD: `${recordData.siteDetails || ''}`,
                cellE: `${recordData.postCode || ''}`,
                cellF: `${recordData.w3w || ''}`,
                cellG: `${recordData.budgetHolder || ''}`,
                cellH: `${scheduleData?.time || ''}`,
                cellI: `${recordData.dateVisited || ''}`,
                cellJ: `${recordData.maintenanceCost || ''}`,
                cellK: `${recordData.poNumber || ''}`,
                cellL: `${recordData.invoiceNo || ''}`,
                cellM: `${recordData.comments2025 || ''}`,
                cellN: `${recordData.additionalInfoComments || ''}`,
              }
            }
          ],
          totalRows: 1
        }
      };
      
      setSelectedEvent(ukpnEvent);
    }
    // For Southeastern events, show detailed information
    else if (event.extendedProps?.isSoutheasternEvent) {
      
      const recordData = event.extendedProps.recordData;
      const scheduleData = event.extendedProps.scheduleData;
      
      // Create a detailed event for display
      const southeasternEvent = {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          isSoutheasternEvent: true,
          contentType: 'southeastern',
          sheetName: 'Southeastern Schedule',
          rows: [
            {
              rowIndex: 1,
              cellData: {
                cellA: `${recordData.dateReceived || ''}`,
                cellB: `${recordData.dateScheduled || ''}`,
                cellC: `${recordData.priorities || ''}`,
                cellD: `${recordData.taylorTechnologyNumber || ''}`,
                cellE: `${recordData.clientReferenceNo || ''}`,
                cellF: `${recordData.locations || ''}`,
                cellG: `${scheduleData?.time || ''}`,
                cellH: `${recordData.siteDetails || ''}`,
                cellI: `${recordData.additionalNotes || ''}`,
                cellJ: `${recordData.quotationNo || ''}`,
                cellK: `${recordData.value || ''}`,
                cellL: `${recordData.jobNo || ''}`,
                cellM: `${recordData.station || ''}`,
                cellN: `${recordData.descriptionOfWorks || ''}`,
                cellO: `${recordData.chNotes || ''}`,
                cellP: `${recordData.simonNotes || ''}`,
                cellQ: `${recordData.returnVisits ? 'Yes' : 'No'}`,
                cellR: `${recordData.cherryPickerRequired ? 'Yes' : 'No'}`,
                cellS: `${recordData.returnSiteDetails || ''}`,
                cellT: `${recordData.returnAdditionalNotes || ''}`,
                cellU: `${recordData.returnForImages || ''}`,
                cellV: `${recordData.ppmComment || ''}`
              }
            }
          ],
          totalRows: 1
        }
      };
      
      setSelectedEvent(southeasternEvent);
    }
    // For Row/Column events, ensure they have proper table structure
    else if (event.extendedProps?.isRowcolEvent || event.extendedProps?.contentType === 'rowcol') {
      
      // Get rows data from various possible sources
      let rowsData = event.extendedProps?.rows || event.dynamicRows || [];
      
      // If no rows data, create a basic row from the event data
      if (!rowsData || rowsData.length === 0) {
        rowsData = [{
          rowIndex: 1,
          cellData: {
            cellA: event.title ? stripHtmlTags(event.title) : 'Event Title',
            cellB: event.location || '',
            cellC: event.description || '',
            cellD: 'rowcol',
            cellE: event.start ? `${event.start.getFullYear()}-${String(event.start.getMonth() + 1).padStart(2, '0')}-${String(event.start.getDate()).padStart(2, '0')}T${String(event.start.getHours()).padStart(2, '0')}:${String(event.start.getMinutes()).padStart(2, '0')}:${String(event.start.getSeconds()).padStart(2, '0')}` : '',
            cellF: event.guests || ''
          }
        }];
      }
      
      // Create a properly formatted event for table display
      const rowcolEvent = {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          isRowcolEvent: true,
          contentType: 'rowcol',
          isDateBasedSheet: true, // Force table display
          sheetName: event.title || 'Row/Column Event',
          rows: rowsData,
          totalRows: rowsData.length
        }
      };
      
      setSelectedEvent(rowcolEvent);
    } else {
      // For regular events, use as-is
    setSelectedEvent(event);
    }
    
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditEventData({
      title: event.title,
      start: new Date(event.start),
      end: new Date(event.end),
      location: event.location || '',
      description: event.description || '',
      guests: event.guests || '',
      calendar: event.calendar || 'Primary'
    });
    setShowEditModal(true);
    setShowEventModal(false);
  };

  const handleUpdateEvent = () => {
    if (editEventData.title.trim() && editingEvent) {
      setEvents(prev => prev.map(event => 
        event.id === editingEvent.id 
          ? {
              ...event,
              title: editEventData.title,
              start: new Date(editEventData.start),
              end: new Date(editEventData.end),
              location: editEventData.location,
              description: editEventData.description,
              guests: editEventData.guests,
              calendar: editEventData.calendar
            }
          : event
      ));
      setShowEditModal(false);
      setEditingEvent(null);
      showNotification(`Event "${editEventData.title}" updated successfully`, 'success');
      setEditEventData({
        title: '',
        start: new Date(),
        end: new Date(Date.now() + 60 * 60 * 1000),
        location: '',
        description: '',
        guests: '',
        calendar: 'Primary'
      });
    }
  };

  const handleDeleteEvent = (eventId) => {
    const event = events.find(e => e.id === eventId);
    setEventToDelete(event);
    setShowDeleteConfirmation(true);
  };

  // Handle delete Southeastern event
  const handleDeleteSoutheasternEvent = (event) => {
    setEventToDelete({ ...event, type: 'southeastern' });
    setShowDeleteConfirmation(true);
  };

  const handleDeleteUkpnEvent = (event) => {
    setEventToDelete({ ...event, type: 'ukpn' });
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteEvent = async () => {
    if (eventToDelete) {
      // Handle Southeastern event deletion
      if (eventToDelete.type === 'southeastern') {
        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          
          const token = localStorage.getItem("token");
          if (!token) {
            showNotification("Please login again", 'error');
            return;
          }
          
          const config = { headers: { Authorization: `Bearer ${token}` } };
          
          // Get the record ID from the Southeastern event
          const recordId = eventToDelete.extendedProps?.recordData?._id;
          if (!recordId) {
            showNotification("Failed to delete: Record ID not found", 'error');
            return;
          }
          
          // Call the delete API
          await axios.delete(`${baseUrl}/api/southeastern/${recordId}`, config);
          
          // Remove the event from the calendar
          setEvents(prev => prev.filter(event => event.id !== eventToDelete.id));
          setShowEventModal(false);
          setSelectedEvent(null);
          showNotification("Southeastern event deleted successfully", 'success');
          
        } catch (error) {
          console.error("Error deleting Southeastern event:", error);
          showNotification("Failed to delete Southeastern event", 'error');
        }
      }
      // Handle UKPN event deletion
      else if (eventToDelete.type === 'ukpn') {
        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          
          const token = localStorage.getItem("token");
          if (!token) {
            showNotification("Please login again", 'error');
            return;
          }
          
          const config = { headers: { Authorization: `Bearer ${token}` } };
          
          // Get the record ID from the UKPN event
          const recordId = eventToDelete.extendedProps?.recordData?._id;
          if (!recordId) {
            showNotification("Failed to delete: Record ID not found", 'error');
            return;
          }
          
          // Call the delete API
          await axios.delete(`${baseUrl}/api/ukpn/${recordId}`, config);
          
          // Remove the event from the calendar
          setEvents(prev => prev.filter(event => event.id !== eventToDelete.id));
          setShowEventModal(false);
          setSelectedEvent(null);
          showNotification("UKPN event deleted successfully", 'success');
          
        } catch (error) {
          console.error("Error deleting UKPN event:", error);
          showNotification("Failed to delete UKPN event", 'error');
        }
      } else {
        // Handle regular event deletion (existing logic)
        setEvents(prev => prev.filter(event => event.id !== eventToDelete.id));
        setShowEventModal(false);
        setSelectedEvent(null);
        showNotification(`Event "${eventToDelete.title}" deleted successfully`, 'warning');
      }
    }
    setShowDeleteConfirmation(false);
    setEventToDelete(null);
  };

  const cancelDeleteEvent = () => {
    setShowDeleteConfirmation(false);
    setEventToDelete(null);
  };


  const handleMiniCalendarDateClick = (date) => {
    setCurrentDate(date);
    setSelectedDate(date);
  };

  // Direct event creation function
  const handleCreateEventFromButton = async () => {
    try {
      setIsCreatingEvent(true); // Set flag to prevent duplicate reloads
      // Parse the selected date and time
      const [year, month, day] = createEventData.eventDate.split('-').map(Number);
      const [startHour, startMinute] = createEventData.startTime.split(':').map(Number);
      // endTime removed - only using start time
      
      // Check for time conflicts and find next available time
      const availableStartTime = findNextAvailableTime(createEventData.eventDate, createEventData.startTime);
      const [availableStartHour, availableStartMinute] = availableStartTime.split(':').map(Number);
      // Show notification if time was adjusted
      if (availableStartTime !== createEventData.startTime) {
        showNotification(`Time conflict detected! Event moved from ${createEventData.startTime} to ${availableStartTime}`, 'warning');
      }
      
      const eventDate = new Date(year, month - 1, day, availableStartHour, availableStartMinute, 0, 0);
      
      // Format the date as sheet name (e.g., "Mon 15th Jan")
      const sheetName = formatDateAsSheetName(createEventData.eventDate);
      
      // Format dynamic data into Excel structure
      let excelData = null;
      let formattedRows = null; // Store formatted rows for immediate display
      
      
      if (createEventData.contentType === 'rowcol' && createEventData.dynamicRows.length > 0) {
        const rows = createEventData.dynamicRows.map((row, rowIndex) => {
          const cellData = {};
          
          // Add dynamic input values to cellA, cellB, cellC, etc.
          row.inputs.forEach((input, inputIndex) => {
            const cellKey = String.fromCharCode(65 + inputIndex); // A, B, C, D, etc.
            cellData[`cell${cellKey}`] = input.value;
          });
          
          // Add Content Type to cellD (overwrite if it exists)
          cellData.cellD = createEventData.contentType; // 'rowcol'
          
            // Add start time to cellE for the first row only
            if (rowIndex === 0) {
              // Store time in local format to avoid timezone issues
              const year = eventDate.getFullYear();
              const month = String(eventDate.getMonth() + 1).padStart(2, '0');
              const day = String(eventDate.getDate()).padStart(2, '0');
              const hours = String(eventDate.getHours()).padStart(2, '0');
              const minutes = String(eventDate.getMinutes()).padStart(2, '0');
              const seconds = String(eventDate.getSeconds()).padStart(2, '0');
              cellData.cellE = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`; // Local time format
            }
          
          return {
            rowIndex: rowIndex + 1,
            cellData: cellData
          };
        });
        
        excelData = {
          sheetName: sheetName, // Use formatted date as sheet name
          sheetIndex: 0,
          rows: rows
        };
        
        // Store the formatted rows for immediate table display
        formattedRows = rows;
      } else if (createEventData.contentType === 'content' && createEventData.content) {
        // For content type, save the event title in cellA and CKEditor content in cellF
        excelData = {
          sheetName: sheetName, // Use formatted date as sheet name
          sheetIndex: 0,
          rows: [
            {
              rowIndex: 1,
              cellData: {
                cellA: createEventData.title || 'Event Title', // Event Title
                cellB: createEventData.location || '', // Location
                cellC: createEventData.description || '', // Description
                  cellD: createEventData.contentType, // 'content'
                  cellE: `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}T${String(eventDate.getHours()).padStart(2, '0')}:${String(eventDate.getMinutes()).padStart(2, '0')}:${String(eventDate.getSeconds()).padStart(2, '0')}`, // Local time format
                cellF: createEventData.content, // CKEditor content (Paragraph)
                cellG: createEventData.guests || '' // Guests
              }
            }
          ]
        };
      } else {
        // Fallback case - create basic event data
        excelData = {
          sheetName: sheetName, // Use formatted date as sheet name
          sheetIndex: 0,
          rows: [
            {
              rowIndex: 1,
              cellData: {
                cellA: createEventData.title || 'Event Title', // Event Title
                cellB: createEventData.location || '', // Location
                cellC: createEventData.description || '', // Description
                cellD: createEventData.contentType || 'content', // Content Type
                cellE: `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}T${String(eventDate.getHours()).padStart(2, '0')}:${String(eventDate.getMinutes()).padStart(2, '0')}:${String(eventDate.getSeconds()).padStart(2, '0')}`, // Time
                cellF: createEventData.content || '', // CKEditor content (Paragraph)
                cellG: createEventData.guests || '' // Guests
              }
            }
          ]
        };
      }
    
    
        // Use the event title directly from createEventData.title and strip HTML tags
        let eventTitle = 'New Event';
        if (createEventData.title) {
          const cleanTitle = stripHtmlTags(createEventData.title);
          if (cleanTitle && cleanTitle.trim() !== '') {
            eventTitle = cleanTitle.trim();
          }
        }
        
    
        const newEvent = {
          id: `new-event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          title: eventTitle,
          start: eventDate,
          end: new Date(eventDate.getTime() + 60 * 60 * 1000), // Add default 1-hour end time
          location: createEventData.location || '',
          description: createEventData.description || '',
          guests: createEventData.guests || '',
          calendar: 'Primary',
          color: getNextUniqueColor(),
          dynamicRows: createEventData.dynamicRows,
          excelData: excelData, // Add the formatted Excel data
          extendedProps: {
            isRowcolEvent: createEventData.contentType === 'rowcol',
            contentType: createEventData.contentType,
            rows: createEventData.contentType === 'rowcol' ? (formattedRows || createEventData.dynamicRows) : createEventData.dynamicRows,
            uniqueIdentifier: `event-${Date.now()}-${createEventData.contentType}`, // Unique identifier to prevent duplicates
            // For Row/Column events, add properties needed for table display
            ...(createEventData.contentType === 'rowcol' && {
              isDateBasedSheet: true,
              sheetName: eventTitle,
              totalRows: createEventData.dynamicRows.length,
              fileName: selectedFileName || 'New Event'
            })
          }
        };
    
      
      // Save to backend first
      const savedData = await saveEventToBackend(newEvent);
      
      if (savedData) {
        // Add backend ID to the event
        newEvent.backendId = savedData._id;
        newEvent.fileId = savedData._id;
        
        // Also store in extendedProps for easy access during editing
        newEvent.extendedProps.fileId = savedData._id;
        newEvent.extendedProps.excelFileId = savedData._id;
        
        // Add to local state immediately for instant display (avoid duplicates)
        setEvents(prev => {
          // Check if content type has changed and clear previous events of different type
          const currentContentType = newEvent.extendedProps?.isRowcolEvent ? 'rowcol' : 'regular';
          if (lastContentType && lastContentType !== currentContentType) {
            // Clear previous events of different content type and same time slot
            const filteredEvents = prev.filter(event => {
              const eventContentType = event.extendedProps?.isRowcolEvent ? 'rowcol' : 'regular';
              const isSameTimeSlot = event.start.getTime() === newEvent.start.getTime();
              
              // Keep events that are not from new-event- (backend events)
              if (!event.id.startsWith('new-event-')) {
                return true;
              }
              
              // Remove events of different content type in same time slot
              if (eventContentType !== currentContentType && isSameTimeSlot) {
                return false;
              }
              
              return true;
            });
            prev = filteredEvents;
          }
          
          // Enhanced duplicate detection for all events
          const exists = prev.some(event => {
            // Check by ID first
            if (event.id === newEvent.id) {
              return true;
            }
            
            // Check by title and time (more strict)
            if (event.title === newEvent.title && 
                event.start.getTime() === newEvent.start.getTime()) {
              return true;
            }
            
            // For rowcol events, check by contentType and time
            if (newEvent.extendedProps?.isRowcolEvent && 
                event.extendedProps?.isRowcolEvent &&
                event.start.getTime() === newEvent.start.getTime()) {
              return true;
            }
            
            // For regular events, check by contentType and time
            if (!newEvent.extendedProps?.isRowcolEvent && 
                !event.extendedProps?.isRowcolEvent &&
                event.start.getTime() === newEvent.start.getTime()) {
              return true;
            }
            
            return false;
          });
          
          // Also check against the last created events
          const isDuplicateOfLastRowcol = lastCreatedRowcolEvent && 
            lastCreatedRowcolEvent.title === newEvent.title && 
            lastCreatedRowcolEvent.start.getTime() === newEvent.start.getTime();
            
          const isDuplicateOfLastEvent = lastCreatedEvent && 
            lastCreatedEvent.title === newEvent.title && 
            lastCreatedEvent.start.getTime() === newEvent.start.getTime();
          
          if (!exists && !isDuplicateOfLastRowcol && !isDuplicateOfLastEvent) {
            
            // Track events and content type
            setLastCreatedEvent(newEvent);
            setLastContentType(newEvent.extendedProps?.isRowcolEvent ? 'rowcol' : 'regular');
            if (newEvent.extendedProps?.isRowcolEvent) {
              setLastCreatedRowcolEvent(newEvent);
            }
            
            return [...prev, newEvent];
          }
          
          return prev;
        });
        
        // Force refresh of calendar view
        setEventsRefreshKey(prev => prev + 1);
        
        // For rowcol events, skip immediate backend reload to prevent duplicates
        // The event is already in local state and will be synced on next page load
        if (!newEvent.extendedProps?.isRowcolEvent) {
          // Only reload for regular events
        setTimeout(() => {
          loadEventsFromBackend();
          }, 1000);
        } else {
          // Clear the last created events after 5 seconds
          setTimeout(() => {
            setLastCreatedRowcolEvent(null);
            setLastCreatedEvent(null);
            setLastContentType(null);
          }, 5000);
        }
        
        // Close modal and reset form
    setShowCreateEventModal(false);
    setCreateEventData({
      title: '',
      description: '',
      location: '',
          guests: '',
          contentType: 'content',
          content: '',
          rows: 1,
          cols: 1,
          dynamicRows: [],
          eventDate: selectedDate.toISOString().split('T')[0],
          startTime: '09:00'
          // endTime removed
        });
        
    showNotification(`Event "${newEvent.title}" created successfully`, 'success');
      } else {
        showNotification('Failed to save event. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      showNotification('Error creating event. Please try again.', 'error');
    } finally {
      setIsCreatingEvent(false); // Reset flag
    }
  };

  // Function to add a new row
  const addNewRow = () => {
    const newRow = {
      id: Date.now().toString(),
      inputCount: 1,
      inputs: [
        {
          id: Date.now().toString() + '_1',
          value: '',
          placeholder: `Row ${createEventData.dynamicRows.length + 1} - Cell 1`
        }
      ]
    };
    setCreateEventData(prev => ({
      ...prev,
      dynamicRows: [...prev.dynamicRows, newRow]
    }));
  };

  // Function to remove a row
  const removeRow = (rowId) => {
    setCreateEventData(prev => ({
      ...prev,
      dynamicRows: prev.dynamicRows.filter(row => row.id !== rowId)
    }));
  };

  // Function to update input count for a row
  const updateRowInputCount = (rowId, newCount) => {
    setCreateEventData(prev => ({
      ...prev,
      dynamicRows: prev.dynamicRows.map(row => {
        if (row.id === rowId) {
          const currentCount = row.inputs.length;
          let newInputs = [...row.inputs];
          
          if (newCount > currentCount) {
            // Add new inputs
            for (let i = currentCount + 1; i <= newCount; i++) {
              newInputs.push({
                id: Date.now().toString() + '_' + i,
                value: '',
                placeholder: `Row ${prev.dynamicRows.indexOf(row) + 1} - Cell ${i}`
              });
            }
          } else if (newCount < currentCount) {
            // Remove inputs
            newInputs = newInputs.slice(0, newCount);
          }
          
          return {
            ...row,
            inputCount: newCount,
            inputs: newInputs
          };
        }
        return row;
      })
    }));
  };

  // Function to update input value
  const updateInputValue = (rowId, inputId, value) => {
    setCreateEventData(prev => ({
      ...prev,
      dynamicRows: prev.dynamicRows.map(row => 
        row.id === rowId 
          ? {
              ...row,
              inputs: row.inputs.map(input => 
                input.id === inputId ? { ...input, value } : input
              )
            }
          : row
      )
    }));
  };

  // Function to check for time conflicts and find next available time
  const findNextAvailableTime = (date, startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    let checkTime = new Date(date);
    checkTime.setHours(hours, minutes, 0, 0);
    
    
    // Check if there's a conflict at the proposed time
    const hasConflict = events.some(event => {
      const eventStart = new Date(event.start);
      const eventEnd = event.end ? new Date(event.end) : new Date(eventStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end time
      
      // Check if events are on the same date
      const sameDate = eventStart.toDateString() === checkTime.toDateString();
      
      // Check for time overlap - more precise conflict detection
      const timeOverlap = (checkTime >= eventStart && checkTime < eventEnd) ||
                         (checkTime.getTime() === eventStart.getTime()) ||
                         (checkTime.getHours() === eventStart.getHours()); // Same hour conflict
      
      if (sameDate && timeOverlap) {
        return true;
      }
      
      return false;
    });
    
    if (!hasConflict) {
      return startTime;
    }
    
    
    // If there's a conflict, find the next available hour (move to next hour)
    let nextHour = hours + 1;
    while (nextHour < 24) {
      const nextTime = `${nextHour.toString().padStart(2, '0')}:00`; // Always use :00 for cleaner times
      const nextCheckTime = new Date(date);
      nextCheckTime.setHours(nextHour, 0, 0, 0); // Set to exact hour
      
      const hasNextConflict = events.some(event => {
        const eventStart = new Date(event.start);
        const eventEnd = event.end ? new Date(event.end) : new Date(eventStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end time
        
        const sameDate = eventStart.toDateString() === nextCheckTime.toDateString();
        const timeOverlap = (nextCheckTime >= eventStart && nextCheckTime < eventEnd) ||
                           (nextCheckTime.getTime() === eventStart.getTime()) ||
                           (nextCheckTime.getHours() === eventStart.getHours()); // Same hour conflict
        
        return sameDate && timeOverlap;
      });
      
      if (!hasNextConflict) {
        return nextTime;
      }
      nextHour++;
    }
    
    // If no time available today, return original time (user will get a warning)
    return startTime;
  };

  // Function to handle start time change and auto-set end time
  // Function to format date as "Mon 15th Jan"
  const formatDateAsSheetName = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const monthName = months[date.getMonth()];
    
    // Add ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
    const getOrdinalSuffix = (day) => {
      if (day >= 11 && day <= 13) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${dayName} ${day}${getOrdinalSuffix(day)} ${monthName}`;
  };

  const handleStartTimeChange = (newStartTime) => {
    setCreateEventData(prev => ({
      ...prev,
      startTime: newStartTime
    }));
  };

  // Function to save event to backend Excel data table
  const saveEventToBackend = async (eventData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return null;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Get fileName from URL path parameters - need to find the original filename with .xlsx
      const fileName = selectedFileName;
      
      // Find the original filename with .xlsx extension from the events data
      let originalFileName = fileName;
      const existingEvent = events.find(event => {
        const normalizedEventFileName = normalizeFileName(event.extendedProps?.fileName || '');
        return normalizedEventFileName === fileName;
      });
      
      if (existingEvent && existingEvent.extendedProps?.fileName) {
        originalFileName = existingEvent.extendedProps.fileName;
      } else {
        // Fallback: try to find any file that matches when normalized
        const allFiles = events
          .map(event => event.extendedProps?.fileName)
          .filter(Boolean)
          .filter((fileName, index, arr) => arr.indexOf(fileName) === index); // Remove duplicates
        
        for (const file of allFiles) {
          if (normalizeFileName(file) === fileName) {
            originalFileName = file;
            break;
          }
        }
      }

      // Create Excel data structure for the event in exact format
      let rowsToSend = [];
      
      // For row/column events, use the rows from extendedProps
      if (eventData.extendedProps?.isRowcolEvent && eventData.extendedProps?.rows) {
        rowsToSend = eventData.extendedProps.rows.map((row, index) => ({
          rowIndex: row.rowIndex || index + 1,
          cellData: row.cellData || {}
        }));
      } else if (eventData.excelData?.rows) {
        // For other events, use excelData.rows
        rowsToSend = eventData.excelData.rows;
      } else {
        // Fallback to default single row
        rowsToSend = [
          {
            rowIndex: 1,
            cellData: {
              cellA: eventData.title ? stripHtmlTags(eventData.title) : 'Event Title', // Event Title
              cellB: eventData.location || 'Location', // Location
              cellC: eventData.description || 'Description', // Description
              cellD: eventData.excelData?.rows?.[0]?.cellData?.cellD || 'content', // Content Type
              cellE: `${eventData.start.getFullYear()}-${String(eventData.start.getMonth() + 1).padStart(2, '0')}-${String(eventData.start.getDate()).padStart(2, '0')}T${String(eventData.start.getHours()).padStart(2, '0')}:${String(eventData.start.getMinutes()).padStart(2, '0')}:${String(eventData.start.getSeconds()).padStart(2, '0')}`, // Time
              cellF: eventData.excelData?.rows?.[0]?.cellData?.cellF || '', // CKEditor content (Paragraph)
              cellG: eventData.guests || '' // Guests
            }
          }
        ];
      }

      const excelDataPayload = {
        sheetName: eventData.excelData?.sheetName || eventData.extendedProps?.sheetName || 'Calendar Events',
        sheetIndex: 0,
        fileName: originalFileName, // Pass the original fileName with .xlsx to backend
        rows: rowsToSend
      };
      
      

      // Save to Excel data table
      const response = await fetch(baseUrl + '/api/excel-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(excelDataPayload)
      });

      if (response.ok) {
        const savedData = await response.json();
        return savedData;
      } else {
        console.error('❌ Failed to save event to backend:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('❌ Error saving event to backend:', error);
      return null;
    }
  };

  // Function to load events from backend
  const loadEventsFromBackend = async () => {
    try {
      // Skip reload if currently creating an event to prevent duplicates
      if (isCreatingEvent) {
        return;
      }
      
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Check if this is the Southeastern calendar
      if (selectedFileName === 'Southeastern') {
        // Fetch Southeastern data
        const response = await fetch(baseUrl + '/api/southeastern', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const responseData = await response.json();
          const allEvents = [];

          if (responseData.success && responseData.data) {
            const southeasternData = responseData.data;
            
            // Process each Southeastern record
            for (const record of southeasternData) {
              if (record.scheduleTask) {
                try {
                  const scheduleData = JSON.parse(record.scheduleTask);
                  
                  if (scheduleData.scheduleDate) {
                    // Create event based on time and total assignment hours
                    const scheduleDate = new Date(scheduleData.scheduleDate);
                    const baseTime = scheduleData.time || '09:00';
                    
                    // Get assignment time (now single value instead of array)
                    const totalHours = scheduleData.assignmentTime || 1;
                    
                    // Parse the base time
                    const [hours, minutes] = baseTime.split(':');
                    const startTime = new Date(scheduleDate);
                    startTime.setHours(parseInt(hours), parseInt(minutes));
                    
                    // Calculate end time based on total assignment hours
                    const endTime = new Date(startTime);
                    endTime.setHours(startTime.getHours() + totalHours);
                    
                    const event = {
                      id: `${record._id}`,
                      title: `${record.taylorTechnologyNumber || 'TT'} - ${record.clientReferenceNo || 'Ref'}`,
                      start: startTime,
                      end: endTime,
                      location: record.locations || '',
                      description: `Team: ${scheduleData.teamSelection}\nAssignment: ${scheduleData.assignmentTime} hour(s)\nLocation: ${record.locations || ''}\nClient Ref: ${record.clientReferenceNo || ''}`,
                      teamName: scheduleData.teamSelection || 'No Team',
                      extendedProps: {
                        southeasternId: record._id,
                        scheduleData: scheduleData,
                        recordData: record,
                        isSoutheasternEvent: true
                      }
                    };
                    
                    allEvents.push(event);
                  }
                } catch (error) {
                  console.error('Error parsing scheduleTask JSON:', error);
                }
              }
            }
          }
          
          setEvents(allEvents);
          setIsLoading(false);
          return;
        }
      }

      // Check if this is the UKPN calendar
      if (selectedFileName === 'UKPN') {
        // Fetch UKPN data
        const response = await fetch(baseUrl + '/api/ukpn', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const responseData = await response.json();
          const allEvents = [];

          if (responseData.success && responseData.data) {
            const ukpnData = responseData.data;
            
            // Process each UKPN record
            for (const record of ukpnData) {
              if (record.scheduleTask) {
                try {
                  const scheduleData = JSON.parse(record.scheduleTask);
                  
                  if (scheduleData.scheduleDate) {
                    // Create event based on time and total assignment hours
                    const scheduleDate = new Date(scheduleData.scheduleDate);
                    const baseTime = scheduleData.time || '09:00';
                    
                    // Get assignment time (now single value instead of array)
                    const totalHours = scheduleData.assignmentTime || 1;
                    
                    // Parse the base time
                    const [hours, minutes] = baseTime.split(':');
                    const startTime = new Date(scheduleDate);
                    startTime.setHours(parseInt(hours), parseInt(minutes));
                    
                    // Calculate end time based on total assignment hours
                    const endTime = new Date(startTime);
                    endTime.setHours(startTime.getHours() + totalHours);
                    
                    const event = {
                      id: `${record._id}`,
                      title: `${record.area || 'Area'} - ${record.siteDetails || 'Site'}`,
                      start: startTime,
                      end: endTime,
                      location: record.postCode || record.siteDetails || '',
                      description: `Team: ${scheduleData.teamSelection}\nAssignment: ${scheduleData.assignmentTime} hour(s)\nArea: ${record.area || ''}\nSite: ${record.siteDetails || ''}\nPO Number: ${record.poNumber || ''}\nInvoice No: ${record.invoiceNo || ''}`,
                      teamName: scheduleData.teamSelection || 'No Team',
                      extendedProps: {
                        ukpnId: record._id,
                        scheduleData: scheduleData,
                        recordData: record,
                        isUkpnEvent: true
                      }
                    };
                    
                    allEvents.push(event);
                  }
                } catch (error) {
                  console.error('Error parsing scheduleTask JSON:', error);
                }
              }
            }
          }
          
          setEvents(allEvents);
          setIsLoading(false);
          return;
        }
      }

      // Fetch all Excel data files
      const response = await fetch(baseUrl + '/api/excel-data', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        const allEvents = [];


        // Handle different response formats
        let data;
        if (responseData.success && responseData.data) {
          data = responseData.data;
        } else if (Array.isArray(responseData)) {
          data = responseData;
        } else {
          data = responseData;
        }

        // Handle both array and single object responses
        const files = Array.isArray(data) ? data : (data.data ? [data.data] : [data]);
        

        // Process each Excel data file
        for (const file of files) {
          
          // Filter files based on selectedFileName if provided
          // But always include files that don't have a fileName (newly created events)
          if (selectedFileName && file.fileName) {
            // Normalize both filenames for comparison
            const normalizedDbFileName = normalizeFileName(file.fileName);
            const normalizedSelectedFileName = normalizeFileName(selectedFileName);
            
            
            if (normalizedDbFileName !== normalizedSelectedFileName) {
            continue;
            }
          }
          
          // Always process files without fileName (newly created events)
          if (!file.fileName) {
          }
          
          if (file.sheetData && file.sheetData.length > 0) {
            for (const sheet of file.sheetData) {
              
              // Check if sheetName is a date format
              const sheetDate = parseDateFromSheetName(sheet.sheetName);
              
              if (sheetDate) {
                // If sheetName is a date, create ONLY ONE event for the entire sheet
                
                // Try to find the earliest start time from the Excel data
                let eventStart = new Date(sheetDate);
                let eventEnd = new Date(sheetDate);
                
                // Look for start times in the sheet's rows
                let earliestTime = null;
                let latestTime = null;
                
                if (sheet.rows && sheet.rows.length > 0) {
                  for (const row of sheet.rows) {
                    if (row.cellData && row.cellData.cellE) {
                      try {
                        const rowStartTime = new Date(row.cellData.cellE);
                        if (!isNaN(rowStartTime.getTime())) {
                          if (!earliestTime || rowStartTime < earliestTime) {
                            earliestTime = rowStartTime;
                          }
                          if (!latestTime || rowStartTime > latestTime) {
                            latestTime = rowStartTime;
                          }
                        }
                      } catch (error) {
                      }
                    }
                  }
                }
                
                // Use the earliest time found, or default to 9 AM if no time found
                if (earliestTime) {
                  eventStart = new Date(earliestTime);
                  // Set end time to 1 hour after start, or use latest time if available
                  if (latestTime && latestTime > earliestTime) {
                    eventEnd = new Date(latestTime);
                    // Add 1 hour to the latest time to ensure the event covers all activities
                    eventEnd.setHours(eventEnd.getHours() + 1);
                  } else {
                    eventEnd = new Date(eventStart);
                    eventEnd.setHours(eventEnd.getHours() + 1);
                  }
                } else {
                  // Default to 9 AM if no start time found in the data
                  eventStart.setHours(9, 0, 0, 0); // 9 AM
                  eventEnd.setHours(10, 0, 0, 0); // 10 AM
                }
                
                // Create a single event title for the entire sheet
                // Try to get the title from the first row's cellA, fallback to sheet name
                let eventTitle = `${sheet.sheetName} - ${sheet.rows.length} Tasks`;
                
                if (sheet.rows && sheet.rows.length > 0 && sheet.rows[0].cellData && sheet.rows[0].cellData.cellA) {
                  const cellAContent = sheet.rows[0].cellData.cellA;
                  // Strip HTML tags from cellA content to get clean title
                  if (cellAContent && cellAContent.trim() !== '') {
                    const cleanTitle = stripHtmlTags(cellAContent);
                    if (cleanTitle && cleanTitle.trim() !== '') {
                      eventTitle = cleanTitle.trim();
                    } else {
                    }
                  } else {
                  }
                } else {
                }
                
                
                // Create event description with summary
                const eventDescription = `Excel data from ${sheet.sheetName} with ${sheet.rows.length} tasks. Click to view detailed table.`;
                
                const event = {
                  id: `excel-${file._id}-${sheet.sheetIndex}-sheet`,
                  title: eventTitle,
                  start: eventStart,
                  end: eventEnd,
                  color: getSheetColor(sheet.sheetName),
                  location: `${sheet.rows.length} tasks`,
                  description: eventDescription,
                  guests: '',
                  calendar: 'Excel Data',
                  type: 'excel-data',
                  extendedProps: {
                    fileName: file.fileName,
                    sheetName: sheet.sheetName,
                    fileId: file._id,
                    sheetIndex: sheet.sheetIndex,
                    isDateBasedSheet: true,
                    sheetDate: sheetDate.toISOString(),
                    rows: sheet.rows, // All rows from the sheet for table display
                    totalRows: sheet.rows.length
                  }
                };
                
                allEvents.push(event);
              } else {
                // Original logic for non-date sheet names
              if (sheet.rows && sheet.rows.length > 0) {
                // Check if this is a rowcol type event (multiple rows with same time)
                const isRowcolEvent = sheet.rows.some(row => row.cellData && row.cellData.cellD === 'rowcol');
                
                if (isRowcolEvent) {
                  // For rowcol events, find the first row with a time and create ONE event for all rows
                  const firstRowWithTime = sheet.rows.find(row => row.cellData && row.cellData.cellE);
                  
                  if (firstRowWithTime) {
                    const startTime = firstRowWithTime.cellData.cellE;
                    
                    try {
                      // Handle both ISO format and local time format
                      let eventStart;
                      if (startTime.includes('Z') || startTime.includes('+') || startTime.includes('-', 10)) {
                        // ISO format with timezone info
                        eventStart = new Date(startTime);
                      } else {
                        // Local time format (YYYY-MM-DDTHH:MM:SS)
                        eventStart = new Date(startTime);
                      }
                      
                      // Create event title from first row or sheet name
                      let eventTitle = sheet.sheetName || 'Rowcol Event';
                      if (firstRowWithTime.cellData.cellA) {
                        const cleanTitle = stripHtmlTags(firstRowWithTime.cellData.cellA);
                        if (cleanTitle && cleanTitle.trim() !== '') {
                          eventTitle = cleanTitle.trim();
                        }
                      }
                      
                      // Create ONE event for all rows
                      const event = {
                        id: `${file._id}_${sheet.sheetIndex}_rowcol`,
                        title: eventTitle,
                        start: eventStart,
                        end: new Date(eventStart.getTime() + 60 * 60 * 1000), // Add default 1-hour end time
                        location: `${sheet.rows.length} rows`,
                        description: `Rowcol event with ${sheet.rows.length} rows`,
                        guests: '',
                        calendar: 'Primary',
                        color: getSheetColor(sheet.sheetName),
                        backendId: file._id,
                        fileId: file._id,
                        excelData: {
                          sheetName: sheet.sheetName,
                          sheetIndex: sheet.sheetIndex,
                          rows: sheet.rows // All rows for this event
                        },
                        extendedProps: {
                          sheetName: sheet.sheetName,
                          sheetIndex: sheet.sheetIndex,
                          fileId: file._id,
                          isDateBasedSheet: false,
                          isRowcolEvent: true,
                          totalRows: sheet.rows.length,
                          rows: sheet.rows // All rows for table display
                        }
                      };
                      
                      allEvents.push(event);
                    } catch (error) {
                      console.error('Error parsing rowcol event dates:', error);
                    }
                  } else {
                  }
                } else {
                  // Original logic for single-row events
                for (const row of sheet.rows) {
                  if (row.cellData) {
                    // Check if this is an event (has start time)
                    const startTime = row.cellData.cellE;
                    
                      if (startTime) {
                        try {
                          // Handle both ISO format and local time format
                          let eventStart;
                          if (startTime.includes('Z') || startTime.includes('+') || startTime.includes('-', 10)) {
                            // ISO format with timezone info
                            eventStart = new Date(startTime);
                          } else {
                            // Local time format (YYYY-MM-DDTHH:MM:SS)
                            eventStart = new Date(startTime);
                          }
                        
                        // Extract title from cellA and strip HTML tags
                        let eventTitle = sheet.sheetName || 'Event';
                        if (row.cellData.cellA) {
                          const cleanTitle = stripHtmlTags(row.cellData.cellA);
                          if (cleanTitle && cleanTitle.trim() !== '') {
                            eventTitle = cleanTitle.trim();
                          }
                        }
                        
                        // Create event object (no end time)
                        const event = {
                          id: `${file._id}_${sheet.sheetIndex}_${row.rowIndex}`,
                          title: eventTitle,
                          start: eventStart,
                          end: new Date(eventStart.getTime() + 60 * 60 * 1000), // Add default 1-hour end time
                          location: row.cellData.cellB || '',
                          description: row.cellData.cellC || '',
                          guests: row.cellData.cellG || '',
                          calendar: 'Primary',
                            color: getSheetColor(sheet.sheetName),
                          backendId: file._id,
                          fileId: file._id,
                          excelData: {
                            sheetName: sheet.sheetName,
                            sheetIndex: sheet.sheetIndex,
                            rows: [row]
                          },
                          extendedProps: {
                            sheetName: sheet.sheetName,
                            sheetIndex: sheet.sheetIndex,
                            fileId: file._id,
                              isDateBasedSheet: false, // This is NOT a date-based sheet
                            allFields: row.cellData
                          }
                        };
                        
                        
                        allEvents.push(event);
                      } catch (error) {
                        console.error('Error parsing event dates:', error);
                        }
                      }
                    }
                  }
                  } // End of else block for single-row events
                } // End of if (sheet.rows && sheet.rows.length > 0)
              } // End of for (const sheet of file.sheetData)
            } // End of if (file.sheetData && file.sheetData.length > 0)
          } // End of for (const file of files)
        }

        // Remove duplicates based on unique ID
        const uniqueEvents = allEvents.filter((event, index, self) => 
          index === self.findIndex(e => e.id === event.id)
        );


        // Merge backend events with existing local events to preserve newly created ones
        setEvents(prevEvents => {
          // Get events that were created locally (not from backend)
          const localEvents = prevEvents.filter(event => 
            event.id && event.id.startsWith('new-event-') && 
            !uniqueEvents.some(backendEvent => {
              // Check by ID
              if (backendEvent.id === event.id) {
                return true;
              }
              
              // Check by title and time
              if (backendEvent.title === event.title && 
                  backendEvent.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              // For rowcol events, also check by contentType
              if (event.extendedProps?.isRowcolEvent && 
                  backendEvent.extendedProps?.isRowcolEvent &&
                  backendEvent.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              // For regular events, also check by contentType
              if (!event.extendedProps?.isRowcolEvent && 
                  !backendEvent.extendedProps?.isRowcolEvent &&
                  backendEvent.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              return false;
            })
          );
          
          // Combine backend events with local events
          const allEvents = [...uniqueEvents, ...localEvents];
          
          // Remove duplicates based on ID, title, and time
          const mergedEvents = allEvents.filter((event, index, self) => 
            index === self.findIndex(e => {
              // Check by ID first
              if (e.id === event.id) {
                return true;
              }
              
              // Check by title and time
              if (e.title === event.title && 
                  e.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              // For rowcol events, also check by contentType and time
              if (event.extendedProps?.isRowcolEvent && 
                  e.extendedProps?.isRowcolEvent &&
                  e.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              // For regular events, also check by contentType and time
              if (!event.extendedProps?.isRowcolEvent && 
                  !e.extendedProps?.isRowcolEvent &&
                  e.start.getTime() === event.start.getTime()) {
                return true;
              }
              
              return false;
            })
          );
          
          return mergedEvents;
        });
        setEventsRefreshKey(prev => prev + 1); // Force refresh of calendar view
        
        // Show notification for loaded events
        if (uniqueEvents.length > 0) {
          const fileInfo = selectedFileName ? ` from ${selectedFileName}` : '';
          const dateBasedEvents = uniqueEvents.filter(event => event.extendedProps?.isDateBasedSheet);
          if (dateBasedEvents.length > 0) {
            // showNotification(`📅 Loaded ${uniqueEvents.length} events${fileInfo}. ${dateBasedEvents.length} events from date-based sheets.`, 'success');
          } else {
            showNotification(`📅 Loaded ${uniqueEvents.length} events${fileInfo}.`, 'success');
          }
        } else {
          const fileInfo = selectedFileName ? ` for ${selectedFileName}` : '';
          showNotification(`📅 No Excel data found${fileInfo}. Upload Excel files to see events.`, 'info');
        }
      } else {
        console.error('❌ Failed to load events from backend:', response.statusText);
        showNotification('⚠️ Could not fetch Excel data. Please check your connection.', 'warning');
      }
    } catch (error) {
      console.error('❌ Error loading events from backend:', error);
      showNotification('❌ Error loading calendar data. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };




  const days = getDaysForView(currentDate, view);
  const today = new Date();
  const timeSlots = getTimeSlots();
  const currentHour = today.getHours();
  const currentMinute = today.getMinutes();

  return (
    <div className={`google-calendar ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Loading Overlay - Only over horizon-dashboard-content */}
      {isLoading && (
        <div className="calendar-loading-overlay">
          <div className="calendar-loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              {selectedFileName 
                ? `Loading ${selectedFileName.replace('.xlsx', '')}...` 
                : 'Loading calendar data...'
              }
            </div>
          </div>
        </div>
      )}
    
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`notification notification-${notification.type}`}
            onClick={() => removeNotification(notification.id)}
          >
            <div className="notification-content">
              <span className="notification-message">{notification.message}</span>
              <button className="notification-close">×</button>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="calendar-header">
        <div className="header-left">
          <button 
            className="menu-button sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          <div className="calendar-logo">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
            <span>
              {selectedFileName === 'Southeastern' ? 'Southeastern Calendar' : 
               selectedFileName === 'UKPN' ? 'UKPN Calendar' : 
               'Calendar'}
            </span>
          </div>
        </div>
        
        <div className="header-center">
          <button className="today-button" onClick={goToToday}>
            Today
          </button>
          <div className="navigation">
            <button 
              className="nav-button" 
              onClick={() => navigateMonth(-1)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            <button 
              className="nav-button" 
              onClick={() => navigateMonth(1)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </div>
          <h2 className="current-month">{formatDate(currentDate)}</h2>
        </div>
        
          <div className="header-right">
            <div className="view-selector">
            <select value={view} onChange={(e) => setView(e.target.value)}>
              <option value="day">Day</option>
              <option value="2days">2 days</option>
              <option value="3days">3 days</option>
              <option value="4days">4 days</option>
              <option value="5days">5 days</option>
              <option value="6days">6 days</option>
              <option value="7days">7 days</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="agenda">Agenda</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="calendar-content">
        {/* Sidebar */}
        {!sidebarCollapsed && (
          <div className="calendar-sidebar">
          {selectedFileName !== 'Southeastern' && selectedFileName !== 'UKPN' && (
          <button className="create-event-button" onClick={() => setShowCreateEventModal(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Create
          </button>
          )}
          
          <div className="mini-calendar">
            <div className="mini-calendar-header">
              <button onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setMonth(currentDate.getMonth() - 1);
                
                // If selected date exists in the new month, keep it, otherwise set to first day
                const selectedDay = selectedDate.getDate();
                const daysInNewMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
                
                if (selectedDay <= daysInNewMonth) {
                  newDate.setDate(selectedDay);
                } else {
                newDate.setDate(1);
                }
                
                setCurrentDate(newDate);
                setSelectedDate(newDate);
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
              <span>{formatDate(currentDate)}</span>
              <button onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setMonth(currentDate.getMonth() + 1);
                
                // If selected date exists in the new month, keep it, otherwise set to first day
                const selectedDay = selectedDate.getDate();
                const daysInNewMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
                
                if (selectedDay <= daysInNewMonth) {
                  newDate.setDate(selectedDay);
                } else {
                newDate.setDate(1);
                }
                
                setCurrentDate(newDate);
                setSelectedDate(newDate);
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </button>
            </div>
            <div className="mini-calendar-grid">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} className="mini-day-header">{day}</div>
              ))}
              {getDaysInMonth(currentDate).map((day, index) => {
                const dayEvents = day ? getEventsForDate(day) : [];
                const hasEvents = dayEvents.length > 0;
                const isSelected = day && day.toDateString() === selectedDate.toDateString();
                const isToday = day && day.toDateString() === today.toDateString();
                
                // Get the primary event color for selected date
                let selectedColor = null;
                if (isSelected && dayEvents.length > 0) {
                  selectedColor = dayEvents[0].color; // Use first event's color
                }
                
                return (
                  <button
                    key={index}
                    className={`mini-day ${isToday ? 'today' : ''} ${hasEvents ? 'has-events' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => day && handleMiniCalendarDateClick(day)}
                    style={isSelected && selectedColor ? {
                      backgroundColor: isToday ? selectedColor : `${selectedColor}30`,
                      color: isToday ? '#fff' : selectedColor,
                      borderColor: selectedColor
                    } : {}}
                  >
                    {day ? day.getDate() : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="search-section">
            <div className="search-input">
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input type="text" placeholder="Search for people" />
            </div>
          </div>

        </div>
        )}

        {/* Main Calendar Grid */}
        <div className="calendar-main">
          {view === 'agenda' ? (
            <div className="agenda-view">
              <div className="agenda-header">
                <h3>Schedule</h3>
                <div className="agenda-date-range">
                  {formatDate(currentDate)}
                </div>
              </div>
              <div className="agenda-list">
                {events
                  .sort((a, b) => new Date(a.start) - new Date(b.start))
                  .map(event => (
                    <div key={event.id} className="agenda-item" onClick={() => handleEventClick(event)}>
                      <div className="agenda-time">
                        {formatTime(event.start)} - {event.end ? formatTime(event.end) : formatTime(new Date(event.start.getTime() + 60 * 60 * 1000))}
                      </div>
                      <div className="agenda-content">
                        <div className="agenda-title">{event.title}</div>
                        {event.location && <div className="agenda-location">{event.location}</div>}
                        {event.guests && <div className="agenda-guests">{event.guests}</div>}
                      </div>
                      <div 
                        className="agenda-color" 
                        style={{backgroundColor: event.color}}
                      ></div>
                    </div>
                  ))}
              </div>
            </div>
          ) : view === 'year' ? (
            <div className="year-view">
              <div className="year-header">
                <h2>{currentDate.getFullYear()}</h2>
              </div>
              <div className="year-grid">
                {getDaysForView(currentDate, 'year').map((month, index) => (
                  <div key={index} className="year-month">
                    <div className="year-month-header">
                      {month.toLocaleDateString('en-US', { month: 'long' })}
                    </div>
                    <div className="year-month-grid">
                      {getDaysInMonth(month).map((day, dayIndex) => {
                        if (!day) return <div key={dayIndex} className="year-day empty"></div>;
                        const dayEvents = getEventsForDate(day);
                        return (
                          <div 
                            key={dayIndex} 
                            className={`year-day ${day.toDateString() === today.toDateString() ? 'today' : ''}`}
                            onClick={() => handleDateClick(day)}
                          >
                            <div className="year-day-number">{day.getDate()}</div>
                            {dayEvents.length > 0 && (
                              <div className="year-day-events">
                                {dayEvents.slice(0, 2).map(event => (
                                  <div 
                                    key={event.id}
                                    className="year-event-dot"
                                    style={{backgroundColor: event.color}}
                                    title={event.title}
                                  ></div>
                                ))}
                                {dayEvents.length > 2 && (
                                  <div className="year-more-events">+{dayEvents.length - 2}</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : view === 'day' || view === '2days' || view === '3days' || view === '4days' || view === '5days' || view === '6days' || view === '7days' || view === 'week' ? (
            <div className="time-view">
              <div className="time-column">
                <div className="timezone">GMT+05:30</div>
                <div 
                  className="time-slots-container"
                  ref={setTimeSlotsRef}
                >
                  {timeSlots.map(slot => (
                    <div key={slot.hour} className="time-slot">
                      {slot.time}
                    </div>
                  ))}
                </div>
              </div>
              <div className="days-columns">
                <div className="days-header">
                  {days.map((day, index) => {
                    const isSelected = day.toDateString() === selectedDate.toDateString();
                    const isToday = day.toDateString() === today.toDateString();
                    const dayEvents = getEventsForDate(day);
                    
                    // Get the primary event color for selected date
                    let selectedColor = null;
                    if (isSelected && dayEvents.length > 0) {
                      selectedColor = dayEvents[0].color; // Use first event's color
                    }
                    
                    return (
                      <div 
                        key={index} 
                        className={`day-header ${isSelected ? 'selected' : ''}`}
                        style={isSelected && selectedColor ? {
                          backgroundColor: `${selectedColor}30`,
                          borderBottomColor: selectedColor
                        } : {}}
                      >
                        <div 
                          className="day-name"
                          style={isSelected && selectedColor ? {
                            color: selectedColor
                          } : {}}
                        >
                        {day.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                      </div>
                        <div 
                          className={`day-number ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                          style={isSelected && selectedColor ? {
                            backgroundColor: selectedColor,
                            color: '#fff',
                            borderColor: '#fff',
                            boxShadow: `0 0 0 2px ${selectedColor}`
                          } : {}}
                        >
                        {day.getDate()}
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div 
                  className="days-grid"
                  ref={setDaysGridRef}
                  key={`days-grid-${eventsRefreshKey}`}
                >
                  {days.map((day, dayIndex) => (
                    <div key={dayIndex} className="day-column">
                      {timeSlots.map(slot => {
                        // Get events that occur during this time slot
                        const dayEvents = getEventsForDate(day).filter(event => {
                          const eventStart = new Date(event.start);
                          const eventEnd = event.end ? new Date(event.end) : new Date(eventStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end time
                          const slotStart = new Date(day);
                          slotStart.setHours(slot.hour, 0, 0, 0);
                          const slotEnd = new Date(day);
                          slotEnd.setHours(slot.hour + 1, 0, 0, 0);
                          
                          // Check if event overlaps with this time slot
                          return eventStart < slotEnd && eventEnd > slotStart;
                        });
                        
                        // Only show events that start in this hour (to avoid duplicates)
                        const eventsInThisHour = dayEvents.filter(event => {
                          const eventStart = new Date(event.start);
                          return eventStart.getHours() === slot.hour;
                        });
                        
                        const overlappingGroups = groupOverlappingEvents(eventsInThisHour);
                        
                        const isCurrentTime = day.toDateString() === today.toDateString() && 
                                             slot.hour === currentHour;
                        
                        return (
                          <div
                            key={slot.hour}
                            className={`time-cell ${isCurrentTime ? 'current-time' : ''}`}
                          >
                            {isCurrentTime && (
                              <div 
                                className="current-time-indicator"
                                style={{
                                  top: `${(currentMinute / 60) * 100}%`
                                }}
                              ></div>
                            )}
                            {overlappingGroups.map((group, groupIndex) => (
                              <div key={groupIndex} className="overlapping-events">
                                {group.map((event, eventIndex) => {
                                  // Calculate event position and height based on start/end times
                                  const eventStart = new Date(event.start);
                                  const eventEnd = event.end ? new Date(event.end) : new Date(eventStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end time
                                  const startMinutes = eventStart.getHours() * 60 + eventStart.getMinutes();
                                  const endMinutes = eventEnd.getHours() * 60 + eventEnd.getMinutes();
                                  const duration = endMinutes - startMinutes;
                                  
                                  const topPosition = ((startMinutes % 60) / 60) * 100;
                                  const height = (duration / 60) * 100;
                                  
                                  // Determine text color based on background color
                                  const isLightColor = event.color === '#ffeb3b' || event.color === '#cddc39' || event.color === '#ffc107';
                                  const textColor = isLightColor ? '#333' : '#fff';
                                  const textShadow = isLightColor ? '0 1px 2px rgba(255, 255, 255, 0.8)' : '0 1px 2px rgba(0, 0, 0, 0.5)';
                                  
                                  return (
                              <div
                                key={event.id}
                                      className={`event-block ${isDragging && draggedEvent?.id === event.id ? 'dragging' : ''}`}
                                      style={{
                                        backgroundColor: event.color,
                                        color: textColor,
                                        width: group.length > 1 ? `${100 / group.length}%` : '100%',
                                        left: group.length > 1 ? `${(eventIndex * 100) / group.length}%` : '0%',
                                        top: `${topPosition}%`,
                                        height: `${height}%`,
                                        position: 'absolute',
                                        zIndex: 10
                                      }}
                                      draggable
                                      onDragStart={(e) => handleEventDragStart(event, e)}
                                      onDragEnd={handleEventDragEnd}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEventClick(event);
                                }}
                              >
                                      <div 
                                        className="resize-handle resize-top"
                                        onMouseDown={(e) => handleEventResize(event, 'start', e)}
                                      ></div>
                                      <div className="event-content" style={{ color: textColor, textShadow: textShadow }}>
                                        {(() => {
                                          // Determine content type for display
                                          const contentType = event.extendedProps?.allFields?.cellD || 'content';
                                          
                                          if (contentType === 'content') {
                                            // Card format for content type
                                            return (
                                              <div>
                                                <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                                  {event.title}
                                                </div>
                                                {event.location && (
                                                  <div style={{ fontSize: '10px', opacity: 0.9 }}>
                                                    📍 {event.location}
                                                  </div>
                                                )}
                                                {event.guests && (
                                                  <div style={{ fontSize: '10px', opacity: 0.9 }}>
                                                    👥 {event.guests}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          } else {
                                            // Table format for rowcol type
                                            return (
                                              <div>
                                                <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                                  📊 {event.title}
                                                </div>
                                                <div style={{ fontSize: '10px', opacity: 0.9 }}>
                                                  Table Data
                                                </div>
                                              </div>
                                            );
                                          }
                                        })()}
                                      </div>
                                      <div 
                                        className="resize-handle resize-bottom"
                                        onMouseDown={(e) => handleEventResize(event, 'end', e)}
                                      ></div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="calendar-grid">
              <div className="calendar-header-row">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="day-header">{day}</div>
                ))}
              </div>
              <div className="calendar-body">
                {days.map((day, index) => {
                  const dayEvents = getEventsForDate(day);
                  const isToday = day && day.toDateString() === today.toDateString();
                  const isSelected = day && day.toDateString() === selectedDate.toDateString();
                  
                  // Get the primary event color for selected date
                  let selectedColor = null;
                  if (isSelected && dayEvents.length > 0) {
                    selectedColor = dayEvents[0].color; // Use first event's color
                  }
                  
                  return (
                    <div
                      key={index}
                      className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => day && handleDateClick(day)}
                      style={isSelected && selectedColor ? {
                        backgroundColor: `${selectedColor}30`
                      } : {}}
                    >
                      {day && (
                        <>
                          <div className="day-number">{day.getDate()}</div>
                          <div className="day-events">
                            {dayEvents.slice(0, 3).map(event => {
                              const isLightColor = event.color === '#ffeb3b' || event.color === '#cddc39' || event.color === '#ffc107';
                              const textColor = isLightColor ? '#333' : '#fff';
                              const textShadow = isLightColor ? '0 1px 2px rgba(255, 255, 255, 0.8)' : '0 1px 2px rgba(0, 0, 0, 0.5)';
                              
                              return (
                              <div
                                key={event.id}
                                className="event-item"
                                  style={{
                                    backgroundColor: event.color,
                                    color: textColor,
                                    textShadow: textShadow
                                  }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEventClick(event);
                                }}
                              >
                                {event.title}
                              </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="more-events">
                                +{dayEvents.length - 3} more
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Event Details Modal - Modern Design */}
      {showEventModal && selectedEvent && (
        <div className="modal fade show" style={{display:'flex', alignItems:'flex-start', justifyContent:'center', background:'rgba(0,0,0,0.3)', paddingTop:'5vh'}} tabIndex="-1" onClick={() => setShowEventModal(false)}>
          <div className={`modal-dialog modal-xl modal-dialog-scrollable ${selectedEvent.extendedProps?.isSoutheasternEvent ? 'southeastern-modal' : ''} ${selectedEvent.extendedProps?.isUkpnEvent ? 'ukpn-modal' : ''}`} style={{marginTop:'5vh', marginBottom:'auto'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto', borderRadius:'12px', boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
              {/* Modal Header - Bootstrap style matching POW Assessment */}
              {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) ? (
                <div className="modal-header" style={{borderBottom:'2px solid #f0f0f0', padding:'16px 20px', background:'linear-gradient(135deg, #8b1634 0%, #a01a3f 100%)'}}>
                  <h5 className="modal-title" style={{color:'#fff', fontWeight:'600', fontSize:'18px'}}>
                    {selectedEvent.extendedProps?.isSoutheasternEvent ? 'Southeastern' : 'UKPN'}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowEventModal(false)} style={{opacity:0.9}}></button>
                </div>
              ) : (
                <div className="modal-header-modern">
                  <div className="header-content">
                    <div className="event-icon-container">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="event-icon">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                      </svg>
                    </div>
                    <div className="header-text">
                      <h3 
                        className="event-title-modern" 
                        title={selectedEvent?.teamName || selectedEvent.extendedProps?.sheetName || selectedEvent.location || selectedEvent.title}
                        style={{
                          color: selectedEvent?.teamName ? getTeamColor(selectedEvent.teamName) || '#fff' : '#fff'
                        }}
                      >
                        {(() => {
                          // For date-based sheets, show sheet name only
                          if (selectedEvent.extendedProps?.isDateBasedSheet && selectedEvent.extendedProps?.sheetName) {
                            return selectedEvent.extendedProps.sheetName;
                          }
                          // For other events, show location or title
                          return truncateTitle(selectedEvent?.teamName || selectedEvent.location || selectedEvent.title);
                        })()}
                      </h3>
                    </div>
                  </div>
                  <button 
                    className="close-button-modern"
                    onClick={() => setShowEventModal(false)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}

            {/* Modern Body - Conditional Layout */}
            {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) ? (
              <div className="modal-body" style={{padding:'20px'}}>
                <div className="event-details-modern">
                {/* Check if this should be side-by-side (Content Type) or full-width (Table) */}
                {(() => {
                  // Determine if this should be side-by-side layout (Content Type events)
                  const isContentTypeEvent = selectedEvent.extendedProps && 
                    selectedEvent.extendedProps.allFields && 
                    selectedEvent.extendedProps.allFields.cellD === 'content' &&
                    !selectedEvent.extendedProps.isDateBasedSheet;
                  
                  const isDateBasedWithContentType = selectedEvent.extendedProps && 
                    selectedEvent.extendedProps.isDateBasedSheet &&
                    selectedEvent.extendedProps.rows &&
                    selectedEvent.extendedProps.rows.length === 1 &&
                    selectedEvent.extendedProps.rows[0].cellData &&
                    selectedEvent.extendedProps.rows[0].cellData.cellD === 'content';
                  
                   // Force table layout for Southeastern events
                   const isSoutheasternEvent = selectedEvent.extendedProps && 
                     selectedEvent.extendedProps.isSoutheasternEvent;
                   
                   const shouldUseSideBySide = (isContentTypeEvent || isDateBasedWithContentType) && !isSoutheasternEvent;
                  
                  if (shouldUseSideBySide) {
                    // Side-by-Side Layout for Content Type events
                    return (
                      <div className="side-by-side-container">
                  {/* Event Details Card - Left Side */}
                  <div className="detail-card event-details-card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                    </svg>
                      <span className="card-title">{selectedEvent.extendedProps?.isSoutheasternEvent ? 'Schedule Details' : 'Event Details'}</span>
                  </div>
                  <div className="detail-rows">
                    {/* Date & Time Information */}
                    <div className="detail-row-modern">
                      <div className="detail-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label-modern">Start Time</span>
                        <span className="detail-value-modern">
                          {selectedEvent.start ? selectedEvent.start.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'No start time'} at {selectedEvent.start ? formatTime(selectedEvent.start) : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="detail-row-modern">
                      <div className="detail-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label-modern">End Time</span>
                        <span className="detail-value-modern">
                          {selectedEvent.end ? selectedEvent.end.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'No end time'} at {selectedEvent.end ? formatTime(selectedEvent.end) : 'N/A'}
                        </span>
                  </div>
                </div>
                  </div>
                </div>

                  {/* Content Details Card - Right Side */}
                  <div className="detail-card content-details-card">
                    <div className="card-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="currentColor"/>
                      </svg>
                      <span className="card-title">
                        {selectedEvent.extendedProps && 
                         selectedEvent.extendedProps.sheetName ? 
                         `Content Details - ${selectedEvent.extendedProps.sheetName}` : 
                         'Content Details'}
                      </span>
                        <button 
                          className="edit-content-btn"
                        onClick={() => handleEditContentDetails()}
                          title="Edit Content Details"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        <span>Edit</span>
                        </button>
                    </div>
                    <div className="detail-rows">
                      {/* Event Title Field */}
                     

                    {/* Dynamic Form Data - Show Excel data for dynamic form events */}
                {selectedEvent.excelData && (
                      <>
                      {selectedEvent.excelData.rows.map((row, rowIndex) => (
                        <div key={rowIndex} className="detail-row-modern">
                          <div className="detail-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4M9 11V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M9 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="detail-content">
                            <span className="detail-label-modern">Row {row.rowIndex}</span>
                            <div className="detail-value-modern">
                                {Object.entries(row.cellData).map(([cellKey, cellValue]) => {
                                  const cleanValue = stripHtmlTags(cellValue);
                                  if (!cleanValue || cleanValue.trim() === '') return null;
                                  return (
                                <div key={cellKey} className="cell-data-item">
                                      <span className="cell-value">{cleanValue}</span>
                                </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      ))}
                      </>
                )}

                    {/* Data for Non-Date Sheets - Show based on content type */}
                {selectedEvent.extendedProps && 
                 selectedEvent.extendedProps.sheetName && 
                 !selectedEvent.extendedProps.isDateBasedSheet && 
                 selectedEvent.extendedProps.allFields && (
                      <>
                    {(() => {
                      const shouldShowEditor = isEditingNonDateSheet && editingNonDateSheetId === selectedEvent.id;
                      const contentType = selectedEvent.extendedProps.allFields.cellD || 'content';
                      
                      return shouldShowEditor;
                    })() ? (
                      <div className="ckeditor-container">
                        {CKEditor && ClassicEditor ? (
                          <CKEditor
                            editor={ClassicEditor}
                            data={nonDateSheetContent}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setNonDateSheetContent(data);
                            }}
                            config={{
                              toolbar: [
                                'heading', '|',
                                'bold', 'italic', 'underline', '|',
                                'bulletedList', 'numberedList', '|',
                                'outdent', 'indent', '|',
                                'link', '|',
                                'undo', 'redo'
                              ],
                              placeholder: 'Enter content here...'
                            }}
                          />
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            CKEditor is loading...
                          </div>
                        )}
                        <div className="ckeditor-actions">
                          <button 
                            className="save-button"
                            onClick={handleNonDateSheetSave}
                          >
                            Save
                          </button>
                          <button 
                            className="cancel-button"
                            onClick={handleNonDateSheetCancel}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                          <>
                        {(() => {
                          const contentType = selectedEvent.extendedProps.allFields.cellD || 'content';
                          
                          if (contentType === 'content') {
                            // Check if we're in edit mode for content details
                            const isEditingContent = isEditingContentDetails && editingContentDetailsId === selectedEvent.id;
                            
                            if (isEditingContent) {
                              // Show CKEditor edit mode
                              return (
                                <div className="content-edit-container">
                                  {Object.entries(contentDetailsData).map(([key, value]) => {
                                    const fieldLabel = key === 'cellA' ? 'Title' : 
                                                     key === 'cellB' ? 'Location' : 
                                                     key === 'cellC' ? 'Description' : 
                                                     key === 'cellF' ? 'Paragraph' : 
                                                     key === 'cellG' ? 'Guests' : 
                                                     key.replace('cell', 'Field ');
                                    
                                    return (
                                      <div key={key} className="content-edit-field">
                                        <label className="content-edit-label">{fieldLabel}</label>
                                        {/* Use CKEditor for all fields */}
                                        <div className="ckeditor-field">
                                          {CKEditor && ClassicEditor ? (
                                            <CKEditor
                                              editor={ClassicEditor}
                                              data={value || ''}
                                              onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setContentDetailsData(prev => ({...prev, [key]: data}));
                                              }}
                                              config={{
                                                toolbar: [
                                                  'heading', '|',
                                                  'bold', 'italic', 'underline', '|',
                                                  'bulletedList', 'numberedList', '|',
                                                  'outdent', 'indent', '|',
                                                  'link', '|',
                                                  'undo', 'redo'
                                                ],
                                                placeholder: `Enter ${fieldLabel.toLowerCase()} here...`
                                              }}
                                            />
                                          ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                              CKEditor is loading...
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <div className="content-edit-actions">
                                    <button 
                                      className="save-button"
                                      onClick={handleContentDetailsSave}
                                    >
                                      Save
                                    </button>
                                    <button 
                                      className="cancel-button"
                                      onClick={handleContentDetailsCancel}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              );
                            } else {
                            // Card format for content type - show in a nice card layout
                            return (
                              <div className="content-type-card">
                                <div className="content-card-body">
                                  {Object.entries(selectedEvent.extendedProps.allFields).map(([key, value]) => {
                                    const cleanValue = stripHtmlTags(value);
                                    if (!cleanValue || cleanValue.trim() === '' || key === 'cellD' || key === 'cellE') return null;
                                    
                                    return (
                                      <div key={key} className="content-field">
                                        <div className="content-field-label">
                                          {key === 'cellA' ? 'Title' : 
                                           key === 'cellB' ? 'Location' : 
                                           key === 'cellC' ? 'Description' : 
                                           key === 'cellF' ? 'Paragraph' : 
                                           key === 'cellG' ? 'Guests' : 
                                           key.replace('cell', 'Field ')}
                                        </div>
                                        <div className="content-field-value">
                                          {key === 'cellF' && cleanValue.includes('<') ? (
                                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(cleanValue) }} />
                                          ) : (
                                            cleanValue
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                            }
                          } else {
                            // Table format for rowcol type - show as individual fields
                            return (
                          <>
                        {Object.entries(selectedEvent.extendedProps.allFields).map(([key, value]) => {
                              const cleanValue = stripHtmlTags(value);
                              if (!cleanValue || cleanValue.trim() === '') return null;
                          
                          return (
                            <div key={key} className="detail-row-modern">
                              <div className="detail-content">
                                    <span className="detail-value-modern">{cleanValue}</span>
                              </div>
                            </div>
                          );
                        })}
                              </>
                            );
                          }
                        })()}
                          </>
                    )}
                      </>
                )}

                    {/* Excel Data - Show when sheet name contains date */}
                {selectedEvent.extendedProps && 
                 selectedEvent.extendedProps.sheetName && 
                 (selectedEvent.extendedProps.isDateBasedSheet || selectedEvent.extendedProps.isRowcolEvent || selectedEvent.extendedProps.contentType === 'rowcol') && 
                 selectedEvent.extendedProps.rows && 
                 Array.isArray(selectedEvent.extendedProps.rows) && 
                 selectedEvent.extendedProps.rows.length > 0 && (
                  <>
                    {(() => {
                      // Check if all rows have content type
                      const allRowsHaveContentType = selectedEvent.extendedProps.rows.every(row => 
                        row.cellData && row.cellData.cellD === 'content'
                      );
                      
                      if (allRowsHaveContentType && selectedEvent.extendedProps.rows.length === 1) {
                        // Check if we're in edit mode for content details
                        const isEditingContent = isEditingContentDetails && editingContentDetailsId === selectedEvent.id;
                        const row = selectedEvent.extendedProps.rows[0];
                        
                        if (isEditingContent) {
                          // Show CKEditor edit mode for single row
                          return (
                            <div className="content-edit-container">
                              {Object.entries(contentDetailsData).map(([key, value]) => {
                                const fieldLabel = key === 'cellA' ? 'Title' : 
                                                 key === 'cellB' ? 'Location' : 
                                                 key === 'cellC' ? 'Description' : 
                                                 key === 'cellF' ? 'Paragraph' : 
                                                 key === 'cellG' ? 'Guests' : 
                                                 key.replace('cell', 'Field ');
                                
                                // Use normal input for Title and Location, CKEditor for Paragraph
                                const isParagraphField = key === 'cellF';
                                const isTitleOrLocation = key === 'cellA' || key === 'cellB';
                                
                                return (
                                  <div key={key} className="content-edit-field">
                                    <label className="content-edit-label">{fieldLabel}</label>
                                    
                                    {isTitleOrLocation ? (
                                      // Normal input for Title and Location
                                      <input
                                        type="text"
                                        className="content-edit-input"
                                        value={value || ''}
                                        onChange={(e) => {
                                          setContentDetailsData(prev => ({...prev, [key]: e.target.value}));
                                        }}
                                        placeholder={`Enter ${fieldLabel.toLowerCase()} here...`}
                                      />
                                    ) : isParagraphField ? (
                                      // CKEditor only for Paragraph
                                      <div className="ckeditor-field">
                                        {CKEditor && ClassicEditor ? (
                                          <CKEditor
                                            editor={ClassicEditor}
                                            data={value || ''}
                                            onChange={(event, editor) => {
                                              const data = editor.getData();
                                              setContentDetailsData(prev => ({...prev, [key]: data}));
                                            }}
                                            config={{
                                              toolbar: [
                                                'heading', '|',
                                                'bold', 'italic', 'underline', '|',
                                                'bulletedList', 'numberedList', '|',
                                                'outdent', 'indent', '|',
                                                'link', '|',
                                                'undo', 'redo'
                                              ],
                                              placeholder: `Enter ${fieldLabel.toLowerCase()} here...`
                                            }}
                                          />
                                        ) : (
                                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                            CKEditor is loading...
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      // Normal input for other fields (Description, Guests, etc.)
                                      <input
                                        type="text"
                                        className="content-edit-input"
                                        value={value || ''}
                                        onChange={(e) => {
                                          setContentDetailsData(prev => ({...prev, [key]: e.target.value}));
                                        }}
                                        placeholder={`Enter ${fieldLabel.toLowerCase()} here...`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                              <div className="content-edit-actions">
                                <button 
                                  className="save-button"
                                  onClick={handleContentDetailsSave}
                                >
                                  Save
                                </button>
                                <button 
                                  className="cancel-button"
                                  onClick={handleContentDetailsCancel}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          );
                        } else {
                          // Show card format for single content type row
                        return (
                          <div className="content-card-body">
                              {Object.entries(row.cellData || {}).map(([key, value]) => {
                                const cleanValue = stripHtmlTags(value);
                                  if (!cleanValue || cleanValue.trim() === '' || key === 'cellD' || key === 'cellE') return null;
                                
                                return (
                                  <div key={key} className="content-field">
                                    <div className="content-field-label">
                                      {key === 'cellA' ? 'Title' : 
                                       key === 'cellB' ? 'Location' : 
                                       key === 'cellC' ? 'Description' : 
                                       key === 'cellF' ? 'Paragraph' : 
                                       key === 'cellG' ? 'Guests' : 
                                       key.replace('cell', 'Field ')}
                    </div>
                                    <div className="content-field-value">
                                      {key === 'cellF' && cleanValue.includes('<') ? (
                                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(cleanValue) }} />
                                      ) : (
                                        cleanValue
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        );
                        }
                      } else {
                        // Show table format for multiple rows or non-content type
                        return (
                     <div className="react-data-table-container">
                       {/* Table Controls Header */}
                       <div className="table-controls-header">
                         <div className="table-controls-left">
                         <div className="pagination-controls">
                           <span className="pagination-text">Show</span>
                           <select className="pagination-select">
                             <option value="10">10</option>
                             <option value="25">25</option>
                             <option value="50">50</option>
                             <option value="100">100</option>
                           </select>
                             <span className="pagination-text">results per page</span>
                        </div>
                         
                           <div className="table-stats">
                             <span className="stats-text">
                               Total: {selectedEvent.extendedProps.rows.length} records
                             </span>
                           </div>
                         </div>
                         
                         <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                           {/* Team Name Display for Southeastern and UKPN Events */}
                           {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) && (() => {
                             // Get team name from scheduleData in extendedProps
                             const scheduleData = selectedEvent.extendedProps.scheduleData;
                             if (scheduleData && scheduleData.teamSelection) {
                               const teamName = scheduleData.teamSelection;
                                 if (teamName) {
                                   const teamColor = getTeamColor(teamName);
                                   return (
                                     <div style={{ 
                                       display: 'flex', 
                                       alignItems: 'center', 
                                       justifyContent: 'center',
                                       flex: 1,
                                       gap: '8px',
                                       fontSize: '14px',
                                       fontWeight: '600'
                                     }}>
                                       <span style={{ color: '#6c757d' }}>Assigned Team:</span>
                                       <span style={{ 
                                         color: teamColor || '#007bff',
                                         backgroundColor: teamColor ? `${teamColor}15` : '#007bff15',
                                         padding: '4px 12px',
                                         borderRadius: '16px',
                                         fontSize: '12px',
                                         fontWeight: '600',
                                         border: `1px solid ${teamColor ? `${teamColor}30` : '#007bff30'}`,
                                         textTransform: 'uppercase',
                                         letterSpacing: '0.3px'
                                       }}>
                                         {teamName}
                                       </span>
                                     </div>
                                   );
                               }
                             }
                             return null;
                           })()}
                           
                           <div className="search-container">
                             <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                               <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                             </svg>
                           <input
                             type="text"
                               placeholder="Search records..."
                             className="filter-input"
                             value={tableSearchTerm}
                             onChange={(e) => setTableSearchTerm(e.target.value)}
                           />
                           </div>
                           
                           {/* Edit All Button or Save/Cancel Buttons */}
                           {isInlineEditing ? (
                             <div style={{ display: 'flex', gap: '8px' }}>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleSaveInlineEditing}
                                 title="Save all changes"
                                 style={{ background: '#28a745' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Save</span>
                               </button>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleCancelInlineEditing}
                                 title="Cancel editing"
                                 style={{ background: '#dc3545' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Cancel</span>
                               </button>
                             </div>
                           ) : (
                             <button 
                             className="edit-all-btn"
                             onClick={() => handleEditAllRecords()}
                             title="Edit all records"
                             >
                               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                 <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                               </svg>
                             <span>Edit All</span>
                             </button>
                           )}
                         
                        </div>
                      </div>

                       {/* React Data Table */}
                       <div className="react-data-table">
                         <table className="data-table">
                           <thead>
                             <tr className="table-header-row">
                               {getAvailableColumns(filteredTableData).map((column, index) => (
                                 <th key={column} className="table-header-cell sortable">
                                 <div className="header-content">
                                     <span>{getColumnDisplayName(column, filteredTableData)}</span>
                    </div>
                               </th>
                               ))}
                               {/* Delete column header when in edit mode */}
                               {isInlineEditing && (
                                 <th className="table-header-cell" style={{ width: '60px', textAlign: 'center' }}>
                                   <div className="header-content">
                                     <span>Actions</span>
                                   </div>
                                 </th>
                               )}
                             </tr>
                           </thead>
                           
                           <tbody>
                             {filteredTableData.map((row, index) => {
                               // Filter out rows that have no meaningful content
                               const hasAnyContent = getAvailableColumns(filteredTableData).some(column => 
                                 hasCellContent(row.cellData?.[column])
                               );
                               
                               if (!hasAnyContent) return null;
                               
                               return (
                               <tr key={row._id || index} className={`table-data-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                                   {getAvailableColumns(filteredTableData).map((column) => {
                                     let cellValue = row.cellData?.[column];
                                     
                                     // Special handling for Time column (cellG) - get time from scheduleTask
                                     if (column === 'cellG' && row.scheduleTask) {
                                       try {
                                         const scheduleData = JSON.parse(row.scheduleTask);
                                         cellValue = scheduleData.time || '';
                                       } catch (e) {
                                         cellValue = '';
                                       }
                                     }
                                     
                                     return (
                                       <td key={column} className="table-data-cell">
                                         {isInlineEditing ? (
                                           <input
                                             type="text"
                                             value={inlineEditingData[index]?.[column] || ''}
                                               ref={(el) => {
                                                 if (el) {
                                                   inputRefs.current[`${index}-${column}`] = el;
                                                 }
                                               }}
                                               onChange={(e) => handleInputChange(e, index, column)}
                                               onFocus={(e) => {
                                                // Allow normal cursor positioning on focus
                                                // Don't force cursor position, let user control it
                                               }}
                                               onClick={(e) => {
                                                // Allow normal cursor positioning on click
                                                // Don't force cursor to end, let user click anywhere
                                               }}
                                             onKeyDown={(e) => {
                                               // Prevent Enter key from creating new lines
                                               if (e.key === 'Enter') {
                                                 e.preventDefault();
                                                 e.target.blur(); // Save and exit editing
                                               }
                                             }}
                                             className="editable-cell"
                                             style={{
                                               minHeight: '20px',
                                               outline: 'none',
                                               border: 'none',
                                               background: 'transparent',
                                               width: '100%',
                                               padding: '4px',
                                               fontSize: 'inherit',
                                               fontFamily: 'inherit',
                                               color: 'inherit'
                                             }}
                                           />
                                         ) : (
                                           hasCellContent(cellValue) && !shouldHideCell(cellValue) && !shouldHideColumn(column) ? (
                                           <div 
                                             data-html="true"
                                             dangerouslySetInnerHTML={{ 
                                               __html: sanitizeHtmlForRender(cellValue || '') 
                                             }}
                                           />
                                         ) : (
                                           <div></div>
                                         )
                                         )}
                                 </td>
                                     );
                                   })}
                                   {/* Delete button column when in edit mode */}
                                   {isInlineEditing && (
                                     <td className="table-data-cell" style={{ width: '60px', textAlign: 'center' }}>
                                       <span
                                         onClick={() => handleDeleteRowInline(index)}
                                         className="delete-row-icon"
                                         title="Delete this row"
                                         style={{
                                           color: '#dc3545',
                                           cursor: 'pointer',
                                           fontSize: '18px',
                                           fontWeight: 'bold',
                                           display: 'inline-block',
                                           width: '24px',
                                           height: '24px',
                                           lineHeight: '24px',
                                           textAlign: 'center',
                                           borderRadius: '50%',
                                           transition: 'all 0.2s ease'
                                         }}
                                         onMouseEnter={(e) => {
                                           e.target.style.backgroundColor = '#dc3545';
                                           e.target.style.color = 'white';
                                         }}
                                         onMouseLeave={(e) => {
                                           e.target.style.backgroundColor = 'transparent';
                                           e.target.style.color = '#dc3545';
                                         }}
                                       >
                                         ✕
                                       </span>
                                     </td>
                                   )}
                               </tr>
                               );
                             }).filter(Boolean)}
                           </tbody>
                         </table>
                        </div>

                       {/* Table Footer */}
                       <div className="table-footer-modern">
                         <div className="table-info">
                           <span className="info-text">
                             Showing {filteredTableData.length} of {selectedEvent.extendedProps.rows.length} records
                           </span>
                      </div>
                         {filteredTableData.length === 0 && tableSearchTerm && (
                           <div className="no-results">
                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="no-results-icon">
                               <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                             </svg>
                             <span>No records found for "{tableSearchTerm}"</span>
                           </div>
                         )}
                       </div>
                    </div>
                        );
                      }
                    })()}
                  </>
                )}
                    </div>
                  </div>
                </div>
                    );
                  } else {
                    // Full-Width Layout for Table format events
                    return (
                      <div className="full-width-container">
                        {/* Event Details Card - Full Width (Skip for Southeastern and UKPN events) */}
                        {!selectedEvent.extendedProps?.isSoutheasternEvent && !selectedEvent.extendedProps?.isUkpnEvent && (
                        <div className="detail-card full-width-event-details">
                    <div className="card-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                      </svg>
                            <span className="card-title">Event Details</span>
                    </div>
                          <div className="detail-rows">
                            {/* Date & Time Information */}
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">Start Time</span>
                                <span className="detail-value-modern">
                                  {selectedEvent.start ? selectedEvent.start.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  }) : 'No start time'} at {selectedEvent.start ? formatTime(selectedEvent.start) : 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">End Time</span>
                                <span className="detail-value-modern">
                                  {selectedEvent.end ? selectedEvent.end.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  }) : 'No end time'} at {selectedEvent.end ? formatTime(selectedEvent.end) : 'N/A'}
                                </span>
                              </div>
                            </div>
                            {/* Event Title Field */}
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">Event Title</span>
                                <span className="detail-value-modern">
                                  {(() => {
                                    
                                    // Always use the event title, not Excel data
                                    return selectedEvent.title || 'No title';
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}

                        {/* Table Data - Full Width */}
                        {selectedEvent.extendedProps && 
                         selectedEvent.extendedProps.sheetName && 
                         (selectedEvent.extendedProps.isDateBasedSheet || selectedEvent.extendedProps.isRowcolEvent || selectedEvent.extendedProps.isSoutheasternEvent || selectedEvent.extendedProps.isUkpnEvent) && 
                         selectedEvent.extendedProps.rows && 
                         Array.isArray(selectedEvent.extendedProps.rows) && 
                         selectedEvent.extendedProps.rows.length > 0 && (
                          <>
                            {(() => {
                              // Check if all rows have content type
                              const allRowsHaveContentType = selectedEvent.extendedProps.rows.every(row => 
                                row.cellData && row.cellData.cellD === 'content'
                              );
                              
                              if (allRowsHaveContentType && selectedEvent.extendedProps.rows.length === 1 && !selectedEvent.extendedProps.isSoutheasternEvent) {
                                // Show card format for single content type row
                                const row = selectedEvent.extendedProps.rows[0];
                                return (
                                  <div className="content-card-body">
                                    {Object.entries(row.cellData || {}).map(([key, value]) => {
                                      const cleanValue = stripHtmlTags(value);
                                      if (!cleanValue || cleanValue.trim() === '' || key === 'cellD') return null;
                                      
                                      return (
                                        <div key={key} className="content-field">
                                          <div className="content-field-label">
                                            {key === 'cellA' ? 'Title' : 
                                             key === 'cellB' ? 'Location' : 
                                             key === 'cellC' ? 'Description' : 
                                             key === 'cellE' ? 'Time' : 
                                             key === 'cellF' ? 'Paragraph' : 
                                             key === 'cellG' ? 'Guests' : 
                                             key.replace('cell', 'Field ')}
                                          </div>
                                          <div className="content-field-value">
                                            {key === 'cellF' && cleanValue.includes('<') ? (
                                              <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(cleanValue) }} />
                                            ) : (
                                              cleanValue
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              } else {
                                // Show table format for multiple rows or non-content type
                                return (
                     <div className="react-data-table-container">
                       {/* Table Controls Header */}
                       <div className="table-controls-header">
                         <div className="table-controls-left">

                         </div>
                         
                         <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                           {/* Team Name Display for Southeastern and UKPN Events */}
                           {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) && (() => {
                             // Get team name from scheduleData in extendedProps
                             const scheduleData = selectedEvent.extendedProps.scheduleData;
                             if (scheduleData && scheduleData.teamSelection) {
                               const teamName = scheduleData.teamSelection;
                                 if (teamName) {
                                   const teamColor = getTeamColor(teamName);
                                   return (
                                     <div style={{ 
                                       display: 'flex', 
                                       alignItems: 'center', 
                                       justifyContent: 'center',
                                       flex: 1,
                                       gap: '8px',
                                       fontSize: '14px',
                                       fontWeight: '600'
                                     }}>
                                       <span style={{ color: '#6c757d' }}>Assigned Team:</span>
                                       <span style={{ 
                                         color: teamColor || '#007bff',
                                         backgroundColor: teamColor ? `${teamColor}15` : '#007bff15',
                                         padding: '4px 12px',
                                         borderRadius: '16px',
                                         fontSize: '12px',
                                         fontWeight: '600',
                                         border: `1px solid ${teamColor ? `${teamColor}30` : '#007bff30'}`,
                                         textTransform: 'uppercase',
                                         letterSpacing: '0.3px'
                                       }}>
                                         {teamName}
                                       </span>
                                     </div>
                                   );
                               }
                             }
                             return null;
                           })()}
                           
                           <div className="search-container">
                             <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                               <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                             </svg>
                           <input
                             type="text"
                               placeholder="Search records..."
                             className="filter-input"
                             value={tableSearchTerm}
                             onChange={(e) => setTableSearchTerm(e.target.value)}
                           />
                           </div>
                           
                           {/* Edit All Button or Save/Cancel Buttons */}
                           {/* {isInlineEditing ? (
                             <div style={{ display: 'flex', gap: '8px' }}>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleSaveInlineEditing}
                                 title="Save all changes"
                                 style={{ background: '#28a745' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Save</span>
                               </button>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleCancelInlineEditing}
                                 title="Cancel editing"
                                 style={{ background: '#dc3545' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Cancel</span>
                               </button>
                             </div>
                           ) : (
                             <button 
                             className="edit-all-btn"
                             onClick={() => handleEditAllRecords()}
                             title="Edit all records"
                             >
                               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                 <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                               </svg>
                             <span>Edit All</span>
                             </button>
                           )} */}
                        </div>
                      </div>

                       {/* React Data Table */}
                       <div className="react-data-table">
                         <table className="data-table">
                           <thead>
                             <tr className="table-header-row">
                               {getAvailableColumns(filteredTableData).map((column, index) => (
                                 <th key={column} className="table-header-cell sortable">
                                 <div className="header-content m-0 text-white" >
                                     <span>{getColumnDisplayName(column, filteredTableData)}</span>
                    </div>
                               </th>
                               ))}
                               {/* Delete column header when in edit mode */}
                               {isInlineEditing && (
                                 <th className="table-header-cell" style={{ width: '60px', textAlign: 'center' }}>
                                   <div className="header-content">
                                     <span>Actions</span>
                                   </div>
                                 </th>
                               )}
                             </tr>
                           </thead>
                           
                           <tbody>
                             {filteredTableData.map((row, index) => {
                               // Filter out rows that have no meaningful content
                               const hasAnyContent = getAvailableColumns(filteredTableData).some(column => 
                                 hasCellContent(row.cellData?.[column])
                               );
                               
                               if (!hasAnyContent) return null;
                               
                               return (
                               <tr key={row._id || index} className={`table-data-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                                   {getAvailableColumns(filteredTableData).map((column) => {
                                     let cellValue = row.cellData?.[column];
                                     
                                     // Special handling for Time column (cellG) - get time from scheduleTask
                                     if (column === 'cellG' && row.scheduleTask) {
                                       try {
                                         const scheduleData = JSON.parse(row.scheduleTask);
                                         cellValue = scheduleData.time || '';
                                       } catch (e) {
                                         cellValue = '';
                                       }
                                     }
                                     
                                     return (
                                       <td key={column} className="table-data-cell">
                                         {isInlineEditing ? (
                                           <input
                                             type="text"
                                             value={inlineEditingData[index]?.[column] || ''}
                                               ref={(el) => {
                                                 if (el) {
                                                   inputRefs.current[`${index}-${column}`] = el;
                                                 }
                                               }}
                                               onChange={(e) => handleInputChange(e, index, column)}
                                               onFocus={(e) => {
                                                // Allow normal cursor positioning on focus
                                                // Don't force cursor position, let user control it
                                               }}
                                               onClick={(e) => {
                                                // Allow normal cursor positioning on click
                                                // Don't force cursor to end, let user click anywhere
                                               }}
                                             onKeyDown={(e) => {
                                               // Prevent Enter key from creating new lines
                                               if (e.key === 'Enter') {
                                                 e.preventDefault();
                                                 e.target.blur(); // Save and exit editing
                                               }
                                             }}
                                             className="editable-cell"
                                             style={{
                                               minHeight: '20px',
                                               outline: 'none',
                                               border: 'none',
                                               background: 'transparent',
                                               width: '100%',
                                               padding: '4px',
                                               fontSize: 'inherit',
                                               fontFamily: 'inherit',
                                               color: 'inherit'
                                             }}
                                           />
                                         ) : (
                                           hasCellContent(cellValue) && !shouldHideCell(cellValue) && !shouldHideColumn(column) ? (
                                           <div 
                                             data-html="true"
                                             dangerouslySetInnerHTML={{ 
                                               __html: sanitizeHtmlForRender(cellValue || '') 
                                             }}
                                           />
                                         ) : (
                                           <div></div>
                                         )
                                         )}
                                 </td>
                                     );
                                   })}
                                   {/* Delete button column when in edit mode */}
                                   {isInlineEditing && (
                                     <td className="table-data-cell" style={{ width: '60px', textAlign: 'center' }}>
                                       <span
                                         onClick={() => handleDeleteRowInline(index)}
                                         className="delete-row-icon"
                                         title="Delete this row"
                                         style={{
                                           color: '#dc3545',
                                           cursor: 'pointer',
                                           fontSize: '18px',
                                           fontWeight: 'bold',
                                           display: 'inline-block',
                                           width: '24px',
                                           height: '24px',
                                           lineHeight: '24px',
                                           textAlign: 'center',
                                           borderRadius: '50%',
                                           transition: 'all 0.2s ease'
                                         }}
                                         onMouseEnter={(e) => {
                                           e.target.style.backgroundColor = '#dc3545';
                                           e.target.style.color = 'white';
                                         }}
                                         onMouseLeave={(e) => {
                                           e.target.style.backgroundColor = 'transparent';
                                           e.target.style.color = '#dc3545';
                                         }}
                                       >
                                         ✕
                                       </span>
                                     </td>
                                   )}
                               </tr>
                               );
                             }).filter(Boolean)}
                           </tbody>
                         </table>
                        </div>

                       {/* Table Footer */}
                       <div className="table-footer-modern">
                         <div className="table-info">
                           <span className="info-text">
                             Showing {filteredTableData.length} of {selectedEvent.extendedProps.rows.length} records
                           </span>
                      </div>
                         {filteredTableData.length === 0 && tableSearchTerm && (
                           <div className="no-results">
                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="no-results-icon">
                               <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                             </svg>
                             <span>No records found for "{tableSearchTerm}"</span>
                           </div>
                         )}
                       </div>
                    </div>
                                );
                              }
                            })()}
                          </>
                )}
                      </div>
                    );
                  }
                })()}
              </div>
              </div>
            ) : (
              <div className="modal-body-modern">
                <div className="event-details-modern">
                {(() => {
                  // Determine if this should be side-by-side layout (Content Type events)
                  const isContentTypeEvent = selectedEvent.extendedProps && 
                    selectedEvent.extendedProps.allFields && 
                    selectedEvent.extendedProps.allFields.cellD === 'content' &&
                    !selectedEvent.extendedProps.isDateBasedSheet;
                  
                  const isDateBasedWithContentType = selectedEvent.extendedProps && 
                    selectedEvent.extendedProps.isDateBasedSheet &&
                    selectedEvent.extendedProps.rows &&
                    selectedEvent.extendedProps.rows.length === 1 &&
                    selectedEvent.extendedProps.rows[0].cellData &&
                    selectedEvent.extendedProps.rows[0].cellData.cellD === 'content';
                  
                   // Force table layout for Southeastern events
                   const isSoutheasternEvent = selectedEvent.extendedProps && 
                     selectedEvent.extendedProps.isSoutheasternEvent;
                   
                   const shouldUseSideBySide = (isContentTypeEvent || isDateBasedWithContentType) && !isSoutheasternEvent;
                  
                  if (shouldUseSideBySide) {
                    // Side-by-Side Layout for Content Type events
                    return (
                      <div className="side-by-side-container">
                  {/* Event Details Card - Left Side */}
                  <div className="detail-card event-details-card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                    </svg>
                      <span className="card-title">{selectedEvent.extendedProps?.isSoutheasternEvent ? 'Schedule Details' : 'Event Details'}</span>
                  </div>
                  <div className="detail-rows">
                    {/* Date & Time Information */}
                    <div className="detail-row-modern">
                      <div className="detail-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label-modern">Start Time</span>
                        <span className="detail-value-modern">
                          {selectedEvent.start ? selectedEvent.start.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'No start time'} at {selectedEvent.start ? formatTime(selectedEvent.start) : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="detail-row-modern">
                      <div className="detail-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label-modern">End Time</span>
                        <span className="detail-value-modern">
                          {selectedEvent.end ? selectedEvent.end.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'No end time'} at {selectedEvent.end ? formatTime(selectedEvent.end) : 'N/A'}
                        </span>
                  </div>
                </div>
                  </div>
                </div>

                  {/* Content Details Card - Right Side */}
                  <div className="detail-card content-details-card">
                    <div className="card-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="currentColor"/>
                      </svg>
                      <span className="card-title">
                        {selectedEvent.extendedProps && 
                         selectedEvent.extendedProps.sheetName ? 
                         `Content Details - ${selectedEvent.extendedProps.sheetName}` : 
                         'Content Details'}
                      </span>
                        <button 
                          className="edit-content-btn"
                        onClick={() => handleEditContentDetails()}
                          title="Edit Content Details"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        <span>Edit</span>
                        </button>
                    </div>
                    <div className="detail-rows">
                      {/* Event Title Field */}
                     

                    {/* Dynamic Form Data - Show Excel data for dynamic form events */}
                {selectedEvent.excelData && (
                      <>
                      {selectedEvent.excelData.rows.map((row, rowIndex) => (
                        <div key={rowIndex} className="detail-row-modern">
                          <div className="detail-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4M9 11V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M9 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="detail-content">
                            <span className="detail-label-modern">Row {row.rowIndex}</span>
                            <div className="detail-value-modern">
                                {Object.entries(row.cellData).map(([cellKey, cellValue]) => {
                                  const cleanValue = stripHtmlTags(cellValue);
                                  if (!cleanValue || cleanValue.trim() === '') return null;
                                  return (
                                <div key={cellKey} className="cell-data-item">
                                      <span className="cell-value">{cleanValue}</span>
                                </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      ))}
                      </>
                )}

                    {/* Data for Non-Date Sheets - Show based on content type */}
                {selectedEvent.extendedProps && 
                 selectedEvent.extendedProps.sheetName && 
                 !selectedEvent.extendedProps.isDateBasedSheet && 
                 selectedEvent.extendedProps.allFields && (
                      <>
                    {(() => {
                      const shouldShowEditor = isEditingNonDateSheet && editingNonDateSheetId === selectedEvent.id;
                      const contentType = selectedEvent.extendedProps.allFields.cellD || 'content';
                      
                      return shouldShowEditor;
                    })() ? (
                      <div className="ckeditor-container">
                        {CKEditor && ClassicEditor ? (
                          <CKEditor
                            editor={ClassicEditor}
                            data={nonDateSheetContent}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setNonDateSheetContent(data);
                            }}
                            config={{
                              toolbar: [
                                'heading', '|',
                                'bold', 'italic', 'underline', '|',
                                'bulletedList', 'numberedList', '|',
                                'outdent', 'indent', '|',
                                'link', '|',
                                'undo', 'redo'
                              ],
                              placeholder: 'Enter content here...'
                            }}
                          />
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            CKEditor is loading...
                          </div>
                        )}
                        <div className="ckeditor-actions">
                          <button 
                            className="save-button"
                            onClick={handleNonDateSheetSave}
                          >
                            Save
                          </button>
                          <button 
                            className="cancel-button"
                            onClick={handleNonDateSheetCancel}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                          <>
                        {(() => {
                          const contentType = selectedEvent.extendedProps.allFields.cellD || 'content';
                          
                          if (contentType === 'content') {
                            // Check if we're in edit mode for content details
                            const isEditingContent = isEditingContentDetails && editingContentDetailsId === selectedEvent.id;
                            
                            if (isEditingContent) {
                              // Show CKEditor edit mode
                              return (
                                <div className="content-edit-container">
                                  {Object.entries(contentDetailsData).map(([key, value]) => {
                                    const fieldLabel = key === 'cellA' ? 'Title' : 
                                                     key === 'cellB' ? 'Location' : 
                                                     key === 'cellC' ? 'Description' : 
                                                     key === 'cellF' ? 'Paragraph' : 
                                                     key === 'cellG' ? 'Guests' : 
                                                     key.replace('cell', 'Field ');
                                    
                                    return (
                                      <div key={key} className="content-edit-field">
                                        <label className="content-edit-label">{fieldLabel}</label>
                                        {/* Use CKEditor for all fields */}
                                        <div className="ckeditor-field">
                                          {CKEditor && ClassicEditor ? (
                                            <CKEditor
                                              editor={ClassicEditor}
                                              data={value || ''}
                                              onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setContentDetailsData(prev => ({...prev, [key]: data}));
                                              }}
                                              config={{
                                                toolbar: [
                                                  'heading', '|',
                                                  'bold', 'italic', 'underline', '|',
                                                  'bulletedList', 'numberedList', '|',
                                                  'outdent', 'indent', '|',
                                                  'link', '|',
                                                  'undo', 'redo'
                                                ],
                                                placeholder: `Enter ${fieldLabel.toLowerCase()} here...`
                                              }}
                                            />
                                          ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                              CKEditor is loading...
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <div className="ckeditor-actions">
                                    <button 
                                      className="save-button"
                                      onClick={handleContentDetailsSave}
                                    >
                                      Save
                                    </button>
                                    <button 
                                      className="cancel-button"
                                      onClick={handleContentDetailsCancel}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              );
                            } else {
                              // Show read-only content
                              return (
                                <>
                                  {Object.entries(selectedEvent.extendedProps.allFields).map(([key, value]) => {
                                    const cleanValue = stripHtmlTags(value);
                                    if (!cleanValue || cleanValue.trim() === '' || key === 'cellD') return null;
                                    
                                    const fieldLabel = key === 'cellA' ? 'Title' : 
                                                       key === 'cellB' ? 'Location' : 
                                                       key === 'cellC' ? 'Description' : 
                                                       key === 'cellF' ? 'Paragraph' : 
                                                       key === 'cellG' ? 'Guests' : 
                                                       key.replace('cell', 'Field ');
                                    
                                    return (
                                      <div key={key} className="detail-row-modern">
                                        <div className="detail-icon">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="currentColor"/>
                                          </svg>
                                        </div>
                                        <div className="detail-content">
                                          <span className="detail-label-modern">{fieldLabel}</span>
                                          <div className="detail-value-modern" dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(value || '') }} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <button 
                                    className="edit-content-btn"
                                    onClick={() => handleEditContentDetails()}
                                    title="Edit Content Details"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Edit</span>
                                  </button>
                                </>
                              );
                            }
                          } else {
                            // Non-content type - show as table or other format
                            return null;
                          }
                        })()}
                          </>
                    )}
                      </>
                )}
                    </div>
                  </div>
                </div>
                      );
                  } else {
                    // Full-Width Layout for Table format events
                    return (
                      <div className="full-width-container">
                        {/* Event Details Card - Full Width (Skip for Southeastern and UKPN events) */}
                        {!selectedEvent.extendedProps?.isSoutheasternEvent && !selectedEvent.extendedProps?.isUkpnEvent && (
                        <div className="detail-card full-width-event-details">
                    <div className="card-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="card-icon">
                              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                      </svg>
                            <span className="card-title">Event Details</span>
                    </div>
                          <div className="detail-rows">
                            {/* Date & Time Information */}
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">Start Time</span>
                                <span className="detail-value-modern">
                                  {selectedEvent.start ? selectedEvent.start.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  }) : 'No start time'} at {selectedEvent.start ? formatTime(selectedEvent.start) : 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" fill="currentColor"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">End Time</span>
                                <span className="detail-value-modern">
                                  {selectedEvent.end ? selectedEvent.end.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  }) : 'No end time'} at {selectedEvent.end ? formatTime(selectedEvent.end) : 'N/A'}
                                </span>
                              </div>
                            </div>
                            {/* Event Title Field */}
                            <div className="detail-row-modern">
                              <div className="detail-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                              <div className="detail-content">
                                <span className="detail-label-modern">Event Title</span>
                                <span className="detail-value-modern">
                                  {(() => {
                                    
                                    // Always use the event title, not Excel data
                                    return selectedEvent.title || 'No title';
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}

                        {/* Table Data - Full Width */}
                        {selectedEvent.extendedProps && 
                         selectedEvent.extendedProps.sheetName && 
                         (selectedEvent.extendedProps.isDateBasedSheet || selectedEvent.extendedProps.isRowcolEvent || selectedEvent.extendedProps.isSoutheasternEvent || selectedEvent.extendedProps.isUkpnEvent) && 
                         selectedEvent.extendedProps.rows && 
                         Array.isArray(selectedEvent.extendedProps.rows) && 
                         selectedEvent.extendedProps.rows.length > 0 && (
                          <>
                            {(() => {
                              // Check if all rows have content type
                              const allRowsHaveContentType = selectedEvent.extendedProps.rows.every(row => 
                                row.cellData && row.cellData.cellD === 'content'
                              );
                              
                              if (allRowsHaveContentType && selectedEvent.extendedProps.rows.length === 1 && !selectedEvent.extendedProps.isSoutheasternEvent) {
                                // Show card format for single content type row
                                const row = selectedEvent.extendedProps.rows[0];
                                return (
                                  <div className="content-card-body">
                                    {Object.entries(row.cellData || {}).map(([key, value]) => {
                                      const cleanValue = stripHtmlTags(value);
                                      if (!cleanValue || cleanValue.trim() === '' || key === 'cellD') return null;
                                      
                                      return (
                                        <div key={key} className="content-field">
                                          <div className="content-field-label">
                                            {key === 'cellA' ? 'Title' : 
                                             key === 'cellB' ? 'Location' : 
                                             key === 'cellC' ? 'Description' : 
                                             key === 'cellE' ? 'Time' : 
                                             key === 'cellF' ? 'Paragraph' : 
                                             key === 'cellG' ? 'Guests' : 
                                             key.replace('cell', 'Field ')}
                                          </div>
                                          <div className="content-field-value">
                                            {key === 'cellF' && cleanValue.includes('<') ? (
                                              <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(cleanValue) }} />
                                            ) : (
                                              cleanValue
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              } else {
                                // Show table format for multiple rows or non-content type
                                return (
                     <div className="react-data-table-container">
                       {/* Table Controls Header */}
                       <div className="table-controls-header">
                         <div className="table-controls-left">

                         </div>
                         
                         <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                           {/* Team Name Display for Southeastern and UKPN Events */}
                           {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) && (() => {
                             // Get team name from scheduleData in extendedProps
                             const scheduleData = selectedEvent.extendedProps.scheduleData;
                             if (scheduleData && scheduleData.teamSelection) {
                               const teamName = scheduleData.teamSelection;
                                 if (teamName) {
                                   const teamColor = getTeamColor(teamName);
                                   return (
                                     <div style={{ 
                                       display: 'flex', 
                                       alignItems: 'center', 
                                       justifyContent: 'center',
                                       flex: 1,
                                       gap: '8px',
                                       fontSize: '14px',
                                       fontWeight: '600'
                                     }}>
                                       <span style={{ color: '#6c757d' }}>Assigned Team:</span>
                                       <span style={{ 
                                         color: teamColor || '#007bff',
                                         backgroundColor: teamColor ? `${teamColor}15` : '#007bff15',
                                         padding: '4px 12px',
                                         borderRadius: '16px',
                                         fontSize: '12px',
                                         fontWeight: '600',
                                         border: `1px solid ${teamColor ? `${teamColor}30` : '#007bff30'}`,
                                         textTransform: 'uppercase',
                                         letterSpacing: '0.3px'
                                       }}>
                                         {teamName}
                                       </span>
                                     </div>
                                   );
                               }
                             }
                             return null;
                           })()}
                           
                           <div className="search-container">
                             <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                               <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                             </svg>
                           <input
                             type="text"
                               placeholder="Search records..."
                             className="filter-input"
                             value={tableSearchTerm}
                             onChange={(e) => setTableSearchTerm(e.target.value)}
                           />
                           </div>
                           
                           {/* Edit All Button or Save/Cancel Buttons */}
                           {/* {isInlineEditing ? (
                             <div style={{ display: 'flex', gap: '8px' }}>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleSaveInlineEditing}
                                 title="Save all changes"
                                 style={{ background: '#28a745' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Save</span>
                               </button>
                               <button 
                                 className="edit-all-btn"
                                 onClick={handleCancelInlineEditing}
                                 title="Cancel editing"
                                 style={{ background: '#dc3545' }}
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                   <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                   <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 <span>Cancel</span>
                               </button>
                             </div>
                           ) : (
                             <button 
                             className="edit-all-btn"
                             onClick={() => handleEditAllRecords()}
                             title="Edit all records"
                             >
                               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                 <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                               </svg>
                             <span>Edit All</span>
                             </button>
                           )} */}
                        </div>
                      </div>

                       {/* React Data Table */}
                       <div className="react-data-table">
                         <table className="data-table">
                           <thead>
                             <tr className="table-header-row">
                               {getAvailableColumns(filteredTableData).map((column, index) => (
                                 <th key={column} className="table-header-cell sortable">
                                 <div className="header-content m-0 text-white" >
                                     <span>{getColumnDisplayName(column, filteredTableData)}</span>
                    </div>
                               </th>
                               ))}
                               {/* Delete column header when in edit mode */}
                               {isInlineEditing && (
                                 <th className="table-header-cell" style={{ width: '60px', textAlign: 'center' }}>
                                   <div className="header-content">
                                     <span>Actions</span>
                                   </div>
                                 </th>
                               )}
                             </tr>
                           </thead>
                           
                           <tbody>
                             {filteredTableData.map((row, index) => {
                               // Filter out rows that have no meaningful content
                               const hasAnyContent = getAvailableColumns(filteredTableData).some(column => 
                                 hasCellContent(row.cellData?.[column])
                               );
                               
                               if (!hasAnyContent) return null;
                               
                               return (
                               <tr key={row._id || index} className={`table-data-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                                   {getAvailableColumns(filteredTableData).map((column) => {
                                     let cellValue = row.cellData?.[column];
                                     
                                     // Special handling for Time column (cellG) - get time from scheduleTask
                                     if (column === 'cellG' && row.scheduleTask) {
                                       try {
                                         const scheduleData = JSON.parse(row.scheduleTask);
                                         cellValue = scheduleData.time || '';
                                       } catch (e) {
                                         cellValue = '';
                                       }
                                     }
                                     
                                     return (
                                       <td key={column} className="table-data-cell">
                                         {isInlineEditing ? (
                                           <input
                                             type="text"
                                             value={inlineEditingData[index]?.[column] || ''}
                                               ref={(el) => {
                                                 if (el) {
                                                   inputRefs.current[`${index}-${column}`] = el;
                                                 }
                                               }}
                                               onChange={(e) => handleInputChange(e, index, column)}
                                               onFocus={(e) => {
                                                // Allow normal cursor positioning on focus
                                                // Don't force cursor position, let user control it
                                               }}
                                               onClick={(e) => {
                                                // Allow normal cursor positioning on click
                                                // Don't force cursor to end, let user click anywhere
                                               }}
                                             onKeyDown={(e) => {
                                               // Prevent Enter key from creating new lines
                                               if (e.key === 'Enter') {
                                                 e.preventDefault();
                                                 e.target.blur(); // Save and exit editing
                                               }
                                             }}
                                             className="editable-cell"
                                             style={{
                                               minHeight: '20px',
                                               outline: 'none',
                                               border: 'none',
                                               background: 'transparent',
                                               width: '100%',
                                               padding: '4px',
                                               fontSize: 'inherit',
                                               fontFamily: 'inherit',
                                               color: 'inherit'
                                             }}
                                           />
                                         ) : (
                                           hasCellContent(cellValue) && !shouldHideCell(cellValue) && !shouldHideColumn(column) ? (
                                           <div 
                                             data-html="true"
                                             dangerouslySetInnerHTML={{ 
                                               __html: sanitizeHtmlForRender(cellValue || '') 
                                             }}
                                           />
                                         ) : (
                                           <div></div>
                                         )
                                         )}
                                 </td>
                                     );
                                   })}
                                   {/* Delete button column when in edit mode */}
                                   {isInlineEditing && (
                                     <td className="table-data-cell" style={{ width: '60px', textAlign: 'center' }}>
                                       <span
                                         onClick={() => handleDeleteRowInline(index)}
                                         className="delete-row-icon"
                                         title="Delete this row"
                                         style={{
                                           color: '#dc3545',
                                           cursor: 'pointer',
                                           fontSize: '18px',
                                           fontWeight: 'bold',
                                           display: 'inline-block',
                                           width: '24px',
                                           height: '24px',
                                           lineHeight: '24px',
                                           textAlign: 'center',
                                           borderRadius: '50%',
                                           transition: 'all 0.2s ease'
                                         }}
                                         onMouseEnter={(e) => {
                                           e.target.style.backgroundColor = '#dc3545';
                                           e.target.style.color = 'white';
                                         }}
                                         onMouseLeave={(e) => {
                                           e.target.style.backgroundColor = 'transparent';
                                           e.target.style.color = '#dc3545';
                                         }}
                                       >
                                         ✕
                                       </span>
                                     </td>
                                   )}
                               </tr>
                               );
                             }).filter(Boolean)}
                           </tbody>
                         </table>
                        </div>

                       {/* Table Footer */}
                       <div className="table-footer-modern">
                         <div className="table-info">
                           <span className="info-text">
                             Showing {filteredTableData.length} of {selectedEvent.extendedProps.rows.length} records
                           </span>
                      </div>
                         {filteredTableData.length === 0 && tableSearchTerm && (
                           <div className="no-results">
                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="no-results-icon">
                               <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                             </svg>
                             <span>No records found for "{tableSearchTerm}"</span>
                           </div>
                         )}
                       </div>
                    </div>
                                );
                              }
                            })()}
                          </>
                )}
                      </div>
                    );
                  }
                })()}
              </div>
              </div>
            )}

            {/* Modern Footer with Action Buttons */}
            {(selectedEvent.extendedProps?.isSoutheasternEvent || selectedEvent.extendedProps?.isUkpnEvent) ? (
              <div className="modal-footer" style={{borderTop:'2px solid #f0f0f0', padding:'16px 20px', display:'flex', justifyContent:'flex-end', gap:'12px'}}>
                <button 
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    if (selectedEvent.extendedProps?.isSoutheasternEvent) {
                      handleDeleteSoutheasternEvent(selectedEvent);
                    } else if (selectedEvent.extendedProps?.isUkpnEvent) {
                      handleDeleteUkpnEvent(selectedEvent);
                    }
                  }}
                  style={{padding:'8px 16px', borderRadius:'6px', fontWeight:'500'}}
                >
                  Delete Event
                </button>
              </div>
            ) : (
              <div className="modal-footer-modern">
                {/* Show delete button for other events if needed */}
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="event-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="create-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="close-button"
                onClick={() => setShowEditModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="event-tabs">
                <button className="tab active">Event</button>
                <button className="tab">Task</button>
                <button className="tab">Appointment schedule (New)</button>
              </div>
              
              <div className="event-form">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Add title"
                    value={editEventData.title}
                    onChange={(e) => setEditEventData(prev => ({...prev, title: e.target.value}))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUpdateEvent();
                      } else if (e.key === 'Escape') {
                        setShowEditModal(false);
                      }
                    }}
                    className="title-input"
                    autoFocus
                  />
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>
                    </svg>
                    <span>
                      {editEventData.start ? editEventData.start.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : 'No start time'} {editEventData.start ? formatTime(editEventData.start) : 'N/A'} - {editEventData.end ? formatTime(editEventData.end) : 'N/A'}
                    </span>
                  </div>
                  <div className="timezone-info">Time zone • Does not repeat</div>
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H16c-.8 0-1.54.37-2.01.99L12 11l-1.99-2.01A2.5 2.5 0 0 0 8 8H5.46c-.8 0-1.54.37-2.01.99L1 15.5V22h2v-6h2.5l2.54-7.63A1.5 1.5 0 0 1 9.46 8H12c.8 0 1.54.37 2.01.99L16 11l1.99-2.01A2.5 2.5 0 0 1 20 8h2.54c.8 0 1.54.37 2.01.99L27 15.5V22h-7z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Add guests"
                      value={editEventData.guests}
                      onChange={(e) => setEditEventData(prev => ({...prev, guests: e.target.value}))}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Add location"
                      value={editEventData.location}
                      onChange={(e) => setEditEventData(prev => ({...prev, location: e.target.value}))}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Add description or a Google Drive attachment"
                      value={editEventData.description}
                      onChange={(e) => setEditEventData(prev => ({...prev, description: e.target.value}))}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    <span>{editEventData.calendar}</span>
                  </div>
                  <div className="calendar-info">Busy • Default visibility • Notify 30 minutes before</div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="more-options">More options</button>
              <button className="save-button" onClick={handleUpdateEvent}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Management Modal */}
      {showGuestModal && (
        <div className="event-modal-overlay" onClick={() => setShowGuestModal(false)}>
          <div className="guest-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Guests</h3>
              <button 
                className="close-button"
                onClick={() => setShowGuestModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="guest-list">
                {guestList.map(guest => (
                  <div key={guest.id} className="guest-item">
                    <div className="guest-info">
                      <div className="guest-name">{guest.name}</div>
                      <div className="guest-email">{guest.email}</div>
                    </div>
                    <div className="guest-rsvp">
                      <select 
                        value={guest.rsvp} 
                        onChange={(e) => updateGuestRSVP(guest.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="accepted">Accepted</option>
                        <option value="declined">Declined</option>
                        <option value="maybe">Maybe</option>
                      </select>
                    </div>
                    <button 
                      className="remove-guest"
                      onClick={() => removeGuest(guest.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="add-guest-form">
                <input 
                  type="email" 
                  placeholder="Add guest email"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      addGuest(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                <button 
                  onClick={(e) => {
                    const input = e.target.previousElementSibling;
                    if (input.value) {
                      addGuest(input.value);
                      input.value = '';
                    }
                  }}
                >
                  Add Guest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && eventToDelete && (
        <div className="event-modal-overlay" onClick={cancelDeleteEvent}>
          <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="confirmation-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>
                {eventToDelete.type === 'southeastern' ? 'Delete Southeastern Event' : 
                 eventToDelete.type === 'ukpn' ? 'Delete UKPN Event' : 
                 'Delete Event'}
              </h3>
            </div>
            
            <div className="confirmation-body">
              <p>Are you sure you want to delete this {
                eventToDelete.type === 'southeastern' ? 'Southeastern event' : 
                eventToDelete.type === 'ukpn' ? 'UKPN event' : 
                'record'}?</p>
              <div className="event-preview">
                <div className="preview-title">
                  {eventToDelete.type === 'southeastern' ? 'Southeastern Event Details:' : 
                   eventToDelete.type === 'ukpn' ? 'UKPN Event Details:' : 
                   'Record Details:'}
                </div>
                <div className="event-details-preview">
                  {eventToDelete.type === 'southeastern' ? (
                    <>
                      {eventToDelete.extendedProps?.recordData?.taylorTechnologyNumber && (
                        <div className="preview-detail">
                          <span className="preview-label">TT No:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.taylorTechnologyNumber}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.recordData?.clientReferenceNo && (
                        <div className="preview-detail">
                          <span className="preview-label">Client Ref:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.clientReferenceNo}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.recordData?.locations && (
                        <div className="preview-detail">
                          <span className="preview-label">Location:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.locations}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.scheduleData?.time && (
                        <div className="preview-detail">
                          <span className="preview-label">Time:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.scheduleData.time}</span>
                        </div>
                      )}
                    </>
                  ) : eventToDelete.type === 'ukpn' ? (
                    <>
                      {eventToDelete.extendedProps?.recordData?.area && (
                        <div className="preview-detail">
                          <span className="preview-label">Area:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.area}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.recordData?.siteDetails && (
                        <div className="preview-detail">
                          <span className="preview-label">Site Details:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.siteDetails}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.recordData?.postCode && (
                        <div className="preview-detail">
                          <span className="preview-label">Post Code:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.recordData.postCode}</span>
                        </div>
                      )}
                      {eventToDelete.extendedProps?.scheduleData?.time && (
                        <div className="preview-detail">
                          <span className="preview-label">Time:</span>
                          <span className="preview-value">{eventToDelete.extendedProps.scheduleData.time}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {(eventToDelete.extendedProps?.sheetName || selectedEvent?.extendedProps?.sheetName) && (
                        <div className="preview-detail">
                          <span className="preview-label">Sheet:</span>
                          <span className="preview-value">{eventToDelete.extendedProps?.sheetName || selectedEvent?.extendedProps?.sheetName}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Show all cell data that will be deleted */}
                  {(() => {
                    const cellData = eventToDelete.extendedProps?.cellData || eventToDelete.row?.cellData || eventToDelete.cellData;
                    if (cellData && Object.keys(cellData).length > 0) {
                      return (
                        <div className="cell-data-preview">
                          {Object.entries(cellData).map(([key, value]) => {
                            if (value && value.trim() !== '') {
                              return (
                                <div key={key} className="preview-detail">
                                  <span className="preview-label">{key.replace('cell', 'Column ')}:</span>
                                  <span className="preview-value" dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(value) }}></span>
                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Fallback for other event types */}
                  {(() => {
                    const cellData = eventToDelete.extendedProps?.cellData || eventToDelete.row?.cellData || eventToDelete.cellData;
                    if (!cellData || Object.keys(cellData).length === 0) {
                      return (
                        <>
                          <div className="preview-detail">
                            <span className="preview-label">Event:</span>
                            <span className="preview-value">{eventToDelete.extendedProps?.cellData?.cellA || eventToDelete.row?.cellData?.cellA || eventToDelete.location || eventToDelete.title || 'Unknown record'}</span>
                          </div>
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
            
            <div className="confirmation-footer">
              <button 
                className="cancel-button"
                onClick={cancelDeleteEvent}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-button"
                onClick={eventToDelete?.type === 'tableRow' || eventToDelete?.type === 'schedulingRow' ? handleConfirmDeleteRow : confirmDeleteEvent}
              >
                {eventToDelete?.type === 'tableRow' ? 'Delete Record' : 
                 eventToDelete?.type === 'schedulingRow' ? 'Delete Scheduling' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal with CKEditor */}
      {showCreateEventModal && (
        <div className="event-modal-overlay" onClick={() => setShowCreateEventModal(false)}>
          <div className="create-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add a schedule</h3>
              <button 
                className="close-button"
                onClick={() => setShowCreateEventModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="event-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      placeholder="Enter event title"
                      value={createEventData.title}
                      onChange={(e) => setCreateEventData(prev => ({...prev, title: e.target.value}))}
                      className="title-input"
                      autoFocus
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      value={createEventData.eventDate}
                      onChange={(e) => setCreateEventData(prev => ({...prev, eventDate: e.target.value}))}
                      className="date-input"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      placeholder="Enter location"
                      value={createEventData.location}
                      onChange={(e) => setCreateEventData(prev => ({...prev, location: e.target.value}))}
                    />
                  </div>
                  </div>
                  
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Start Time</label>
                    <input
                      type="time"
                      value={createEventData.startTime}
                      onChange={(e) => handleStartTimeChange(e.target.value)}
                      className="time-input"
                    />
                </div>
                
                  {/* End Time field removed - only using start time */}
                </div>

                {/* Content Type Selection */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Content Type</label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="contentType"
                          value="content"
                          checked={createEventData.contentType === 'content'}
                          onChange={(e) => setCreateEventData(prev => ({...prev, contentType: e.target.value}))}
                        />
                        <span className="radio-label">Paragraph</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="contentType"
                          value="rowcol"
                          checked={createEventData.contentType === 'rowcol'}
                          onChange={(e) => setCreateEventData(prev => ({...prev, contentType: e.target.value}))}
                        />
                        <span className="radio-label">Row/Column</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Conditional Content Based on Selection */}
                {createEventData.contentType === 'content' && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label className="form-label">Content</label>
                      <div className="ckeditor-container">
                        {CKEditor && ClassicEditor ? (
                          <CKEditor
                            editor={ClassicEditor}
                            data={createEventData.content}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setCreateEventData(prev => ({...prev, content: data}));
                            }}
                            config={{
                              toolbar: [
                                'heading', '|',
                                'bold', 'italic', 'link', '|',
                                'bulletedList', 'numberedList', '|',
                                'outdent', 'indent', '|',
                                'blockQuote', 'insertTable', '|',
                                'undo', 'redo'
                              ]
                            }}
                          />
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            CKEditor is loading...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {createEventData.contentType === 'rowcol' && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label className="form-label">Dynamic Rows with Input Boxes</label>
                      
                      {/* Dynamic Rows Container */}
                      <div className="dynamic-rows-container">
                        {createEventData.dynamicRows.length === 0 ? (
                          <div className="empty-state">
                            <p>No rows added yet</p>
                            <p>Click "Add New Row" to start</p>
                          </div>
                        ) : (
                          <div className="dynamic-rows-list">
                            {createEventData.dynamicRows.map((row, rowIndex) => (
                              <div key={row.id} className="dynamic-row-item">
                                <div className="row-header">
                                  <div className="row-title">
                                    <span className="row-label">Row {rowIndex + 1}</span>
                                    <span className="input-count">({row.inputs.length} cell{row.inputs.length !== 1 ? 's' : ''})</span>
                                  </div>
                                  <div className="row-controls">
                                    <div className="input-count-control">
                                      <label>Cells:</label>
                                      <div className="count-buttons">
                                        <button
                                          type="button"
                                          className="count-btn decrease"
                                          onClick={() => updateRowInputCount(row.id, Math.max(1, row.inputCount - 1))}
                                          disabled={row.inputCount <= 1}
                                        >
                                          -
                                        </button>
                                        <span className="count-display">{row.inputCount}</span>
                                        <button
                                          type="button"
                                          className="count-btn increase"
                                          onClick={() => updateRowInputCount(row.id, Math.min(10, row.inputCount + 1))}
                                          disabled={row.inputCount >= 10}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="remove-row-btn"
                                      onClick={() => removeRow(row.id)}
                                      title="Remove this row"
                                    >
                                      Remove Row
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="row-inputs">
                                  {row.inputs.map((input, inputIndex) => (
                                    <div key={input.id} className="input-item">
                                      <input
                                        type="text"
                                        placeholder={input.placeholder}
                                        value={input.value}
                                        onChange={(e) => updateInputValue(row.id, input.id, e.target.value)}
                                        className="row-input-box"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="dynamic-rows-actions">
                          <button
                            type="button"
                            className="add-row-btn"
                            onClick={addNewRow}
                          >
                            Add New Row
                          </button>
                          <button
                            type="button"
                            className="clear-all-btn"
                            onClick={() => setCreateEventData(prev => ({...prev, dynamicRows: []}))}
                            disabled={createEventData.dynamicRows.length === 0}
                          >
                            Clear All Rows
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowCreateEventModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-button" 
                onClick={handleCreateEventFromButton}
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Edit Modal */}
      {showTableEditModal && (
        <div className="event-modal-overlay" onClick={() => setShowTableEditModal(false)}>
          <div className="edit-modal-modern large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-modern">
              <div className="header-content">
                <div className="event-icon-container">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="event-icon">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="header-text">
                  <h3 className="event-title-modern">Edit All Records</h3>
                  <div className="event-type-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="currentColor"/>
                    </svg>
                    <span>{filteredTableData.length} Records</span>
                  </div>
                </div>
              </div>
              <button 
                className="close-button-modern"
                onClick={() => setShowTableEditModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                </svg>
              </button>
            </div>

            <div className="modal-content-modern">
              <div className="all-records-edit-container">
                <div className="records-edit-list">
                  {filteredTableData.map((row, index) => {
                    const hasOnlyCellA = Object.keys(row.cellData || {}).length === 1 && row.cellData.cellA;
                    const cellCount = Object.keys(row.cellData || {}).length;
                    
                    return (
                      <div key={row._id || index} className="record-edit-item">
                        <div className="record-edit-header">
                          <div className="record-info">
                            <span className="record-index">Record {index + 1}</span>
                            <span className="record-type">
                              {hasOnlyCellA ? 'Single Cell (Rich Text)' : `Multi Cell (${cellCount} fields)`}
                            </span>
                          </div>
                          <button 
                            className="save-record-btn"
                            onClick={() => handleSaveRecord(row, index)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                            </svg>
                            Save
                          </button>
                        </div>
                        
                        <div className="record-edit-form">
                          {Object.entries(row.cellData || {}).map(([key, value]) => {
                            const isSingleCell = hasOnlyCellA && key === 'cellA';
                    
                    return (
                              <div key={key} className="form-group">
                        <label className="form-label">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="label-icon">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="currentColor"/>
                          </svg>
                                  {key.replace('cell', 'Column ')}
                        </label>
                                {isSingleCell ? (
                                  // Use CKEditor for single cellA
                                  <div className="ckeditor-container">
                                    {CKEditor && ClassicEditor ? (
                                      <CKEditor
                                        editor={ClassicEditor}
                                        data={value || ''}
                                        onChange={(event, editor) => {
                                          const data = editor.getData();
                                          // Update the specific row's data
                                          const updatedRows = [...filteredTableData];
                                          updatedRows[index] = {
                                            ...updatedRows[index],
                                            cellData: {
                                              ...updatedRows[index].cellData,
                                              [key]: data
                                            }
                                          };
                                          // Update the selected event
                                          setSelectedEvent({
                                            ...selectedEvent,
                                            extendedProps: {
                                              ...selectedEvent.extendedProps,
                                              rows: updatedRows
                                            }
                                          });
                                        }}
                                        config={{
                                          toolbar: [
                                            'heading', '|',
                                            'bold', 'italic', 'underline', 'strikethrough', '|',
                                            'link', 'bulletedList', 'numberedList', '|',
                                            'outdent', 'indent', '|',
                                            'blockQuote', 'insertTable', '|',
                                            'undo', 'redo'
                                          ],
                                          placeholder: 'Enter content here...'
                                        }}
                          />
                        ) : (
                                      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                        CKEditor is loading...
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  // Use regular input for multi-cell objects
                          <input
                            type="text"
                            className="form-input"
                                    value={value || ''}
                                    onChange={(e) => {
                                      // Update the specific row's data
                                      const updatedRows = [...filteredTableData];
                                      updatedRows[index] = {
                                        ...updatedRows[index],
                                        cellData: {
                                          ...updatedRows[index].cellData,
                                          [key]: e.target.value
                                        }
                                      };
                                      // Update the selected event
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        extendedProps: {
                                          ...selectedEvent.extendedProps,
                                          rows: updatedRows
                                        }
                                      });
                                    }}
                                    placeholder={`Enter ${key.replace('cell', 'column ')}`}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="modal-footer-modern">
              <button 
                className="cancel-button-modern"
                onClick={() => setShowTableEditModal(false)}
              >
                Close
              </button>
              <button 
                className="save-all-button-modern"
                onClick={handleSaveAllRecords}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                </svg>
                Save All Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduling Modal with CKEditor */}
      {showSchedulingModal && (
        <div className="event-modal-overlay" onClick={handleCloseSchedulingModal}>
          <div className="scheduling-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-modern">
              <div className="header-content">
                <div className="event-icon-container">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="event-icon">
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="header-text">
                  <h3 className="event-title-modern">
                    {schedulingMode === 'add' ? 'Add New Scheduling' : 'Edit Scheduling'}
                  </h3>
                  <div className="event-type-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="currentColor"/>
                    </svg>
                    <span>Scheduling Record</span>
                  </div>
                </div>
              </div>
              <button 
                className="close-button-modern"
                onClick={handleCloseSchedulingModal}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="modal-body-modern">
              <div className="scheduling-form">
                {/* Basic Fields */}
                <div className="form-section">
                  <h4 className="section-title">Basic Information</h4>
                  <div className="form-grid-full">
                    {schedulingData.cellA && schedulingData.cellA.trim() !== '' && (
                      <div className="form-group">
                        <label className="form-label">Cell A</label>
                        <div className="ckeditor-container">
                          <ReactQuill
                            value={schedulingData.cellA}
                            onChange={handleCellAChange}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'indent': '-1'}, { 'indent': '+1' }],
                                ['link', 'image'],
                                ['clean']
                              ]
                            }}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    )}
                    {schedulingData.cellB && schedulingData.cellB.trim() !== '' && (
                      <div className="form-group">
                        <label className="form-label">Cell B</label>
                        <div className="ckeditor-container">
                          <ReactQuill
                            value={schedulingData.cellB}
                            onChange={handleCellBChange}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'indent': '-1'}, { 'indent': '+1' }],
                                ['link', 'image'],
                                ['clean']
                              ]
                            }}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    )}
                    {schedulingData.cellC && schedulingData.cellC.trim() !== '' && (
                      <div className="form-group">
                        <label className="form-label">Cell C</label>
                        <div className="ckeditor-container">
                          <ReactQuill
                            value={schedulingData.cellC}
                            onChange={handleCellCChange}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'indent': '-1'}, { 'indent': '+1' }],
                                ['link', 'image'],
                                ['clean']
                              ]
                            }}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    )}
                    {schedulingData.cellD && schedulingData.cellD.trim() !== '' && (
                      <div className="form-group">
                        <label className="form-label">Cell D</label>
                        <div className="ckeditor-container">
                          <ReactQuill
                            value={schedulingData.cellD}
                            onChange={handleCellDChange}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'indent': '-1'}, { 'indent': '+1' }],
                                ['link', 'image'],
                                ['clean']
                              ]
                            }}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    )}
                    {schedulingData.cellE && schedulingData.cellE.trim() !== '' && (
                      <div className="form-group">
                        <label className="form-label">Cell E</label>
                        <div className="ckeditor-container">
                          <ReactQuill
                            value={schedulingData.cellE}
                            onChange={handleCellEChange}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'indent': '-1'}, { 'indent': '+1' }],
                                ['link', 'image'],
                                ['clean']
                              ]
                            }}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            <div className="modal-footer-modern">
              <button 
                className="cancel-button-modern"
                onClick={handleCloseSchedulingModal}
              >
                Cancel
              </button>
              <button 
                className="save-button-modern"
                onClick={handleSaveScheduling}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                </svg>
                {schedulingMode === 'add' ? 'Add Scheduling' : 'Update Scheduling'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarComponent;